import React, {
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  createNuevoDiseno,
  vectorizeDesignSvg,
  saveDesignToDrive,
  getImageEngines,
  checkImageEngines,
  activateImageEngine,
  dismissImageEngine,
  clearImageEnginesCreditsBlock,
  clearOpenAiCreditsBlock,
} from "../../api/AI/presentacionesApi";
import SaveDesignToDriveDialog from "./SaveDesignToDriveDialog";
import ReplaceDesignRefDialog from "./ReplaceDesignRefDialog";
import { bob, cloudLift, motionSafe } from "./studioDialogMotion";
import { SidebarContext } from "../Sidebar/SidebarProvider";

import {
  Box,
  TextField,
  Button,
  CardMedia,
  Typography,
  Link,
  IconButton,
  Alert,
  CircularProgress,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  addSkillAgentMoodboardAssets,
  createSkillAgentMoodboard,
  getSkillAgentMoodboardById,
  listSkillAgentMoodboards,
  saveSkillAgentMoodboardPages,
  updateSkillAgentMoodboard,
} from "../../api/Skills/skillAgentMoodboardsApi";
import { resolveSkillAgentCaratulaForCreate } from "../../api/Skills/skillAgentCaratulasApi";
import {
  buildFichaTecnicaPages,
  clampSlot,
  normalizePages,
  newSlotId,
} from "../Skills/skillAgentMoodboardLayout";
import { isFichaTecnicaBoard } from "../Skills/skillAgentTaskTree";
import CreateFichaTecnicaDialog from "./CreateFichaTecnicaDialog";
import ConfirmHistoryDeleteDialog from "./ConfirmHistoryDeleteDialog";
import {
  coerceMoodboardPages,
  collectFichaAttachSources,
  filesFromHistoryRefs,
  placeFichaAssetsOnPages,
  placeMoodboardAssetsOnPages,
} from "./aiDesignsFichaAttach";
import {
  AI_DESIGN_HISTORY_LIMIT,
  AI_DESIGN_HISTORY_RETENTION_DAYS,
  acknowledgeRetentionNoticeInSummary,
  buildAiDesignStudioSummary,
  parseAiDesignStudioSummary,
  sanitizeStudioPrompt,
  selectPendingHistoryForBoard,
} from "./aiDesignsStudioState";
import HistoryRetentionDialog from "./HistoryRetentionDialog";
import { alpha, useTheme } from "@mui/material/styles";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import CloseIcon from "@mui/icons-material/Close";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DeleteSweepOutlinedIcon from "@mui/icons-material/DeleteSweepOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useTranslation } from "react-i18next";
import {
  buildSketchPrompt,
  sketchViewsForChoice,
} from "./nuevoDisenoSketchPrompt";
import NuevoDisenoSketchViewDialog from "./NuevoDisenoSketchViewDialog";
import NuevoDisenoHelpDialog from "./NuevoDisenoHelpDialog";
import AiDesignsGenerarDialog from "./AiDesignsGenerarDialog";

const STUDIO_BLUE = "#1473e6";

function useStudioTokens() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  return useMemo(() => {
    const text = theme.palette.text.primary;
    const muted = isDark ? "#a3a3a3" : alpha(theme.palette.text.primary, 0.62);
    const line = theme.palette.divider;
    const softBorder = isDark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.16)";
    const softBorderStrong = isDark
      ? "rgba(255,255,255,0.35)"
      : "rgba(0,0,0,0.28)";
    const hoverSoft = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)";
    const wellBg = isDark ? "#0c0c0c" : alpha(theme.palette.common.black, 0.04);
    const inputBg = isDark
      ? theme.palette.background.cajaInpunts || "#181818"
      : theme.palette.background.paper;
    return {
      isDark,
      text,
      muted,
      line,
      blue: STUDIO_BLUE,
      pageBg: theme.palette.background.default,
      panelBg: theme.palette.background.paper,
      wellBg,
      footerInner: isDark ? "#1a1a1a" : theme.palette.background.paper,
      promptBg: isDark ? "#121212" : alpha(theme.palette.common.black, 0.03),
      softBorder,
      softBorderStrong,
      hoverSoft,
      disabledFg: isDark ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.3)",
      disabledBorder: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)",
      disabledBtnFg: isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.38)",
      canvasGrad: isDark
        ? "radial-gradient(ellipse at center, #1c1c1c 0%, #0c0c0c 72%)"
        : "radial-gradient(ellipse at center, #ffffff 0%, #ececec 72%)",
      chipLabel: isDark ? "#c9d7ff" : "#1a4a9e",
      chipBorder: isDark ? "rgba(140,180,255,0.45)" : "rgba(20,115,230,0.35)",
      awesome: isDark ? "#8cb4ff" : STUDIO_BLUE,
      fieldSx: {
        "& .MuiInputBase-root": {
          color: text,
          backgroundColor: inputBg,
        },
        "& .MuiInputLabel-root": {
          color: muted,
        },
        "& .MuiInputLabel-root.Mui-focused": {
          color: isDark ? "#8cb4ff" : STUDIO_BLUE,
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: softBorder,
        },
        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: softBorderStrong,
        },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
          {
            borderColor: STUDIO_BLUE,
          },
        "& .MuiSvgIcon-root": { color: muted },
      },
      menuProps: {
        PaperProps: {
          sx: {
            maxHeight: 320,
            bgcolor: "background.paper",
            color: "text.primary",
            "& .MuiMenuItem-root": { color: "text.primary" },
          },
        },
        disableScrollLock: true,
      },
    };
  }, [theme, isDark]);
}

const NUEVO_ENGINES = [
  {
    id: "gpt-image",
    labelKey: "presentacionesSearch.engines.gptName",
    accent: "#f0a202",
  },
  {
    id: "flux",
    labelKey: "presentacionesSearch.engines.fluxName",
    accent: "#ef5b4c",
  },
  {
    id: "nano-banana",
    labelKey: "presentacionesSearch.engines.nanoName",
    accent: "#4c8dff",
  },
];

let nuevoRefSeq = 0;
function makeNuevoRefItems(files) {
  return (files || []).map((file) => ({
    id: `ref-${Date.now()}-${(nuevoRefSeq += 1)}`,
    file,
  }));
}

function isImageFile(file) {
  if (!file) return false;
  if ((file.type || "").startsWith("image/")) return true;
  return /\.(png|jpe?g|webp|gif|bmp|heic|heif|avif)$/i.test(file.name || "");
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("read failed"));
    reader.readAsDataURL(file);
  });
}

/** Thumb / preview: data URL de ref, o ruta API de generación. */
function designImageSrc(item, apiUrl) {
  if (!item) return "";
  const local = String(item.previewUrl || "").trim();
  if (local) return local;
  const path = String(item.pngRelativeUrl || item.relativeUrl || "").trim();
  if (!path) return "";
  if (/^https?:\/\//i.test(path) || path.startsWith("data:")) return path;
  return `${apiUrl}/api${path}`;
}

function dragHasFiles(event) {
  const types = event.dataTransfer?.types;
  if (!types) return false;
  return Array.from(types).includes("Files");
}

const AiDesignsStudioPage = ({
  embedded = false,
  designId: designIdProp = null,
  compassState = null,
  onLeave = null,
  onDesignIdChange = null,
} = {}) => {
  const { contentMargin } = useContext(SidebarContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: routeIdParam } = useParams();
  const s = useStudioTokens();

  const compassIn =
    (compassState && typeof compassState === "object" ? compassState : null) ||
    (location.state?.fromCompass && typeof location.state === "object"
      ? location.state
      : null);
  const routeAiDesignId = (() => {
    const fromProp = Number(designIdProp);
    if (Number.isFinite(fromProp) && fromProp > 0) return fromProp;
    const fromRoute = Number(routeIdParam);
    if (Number.isFinite(fromRoute) && fromRoute > 0) return fromRoute;
    const fromState = Number(compassIn?.AiDesignId || compassIn?.MoodboardId);
    return Number.isFinite(fromState) && fromState > 0 ? fromState : null;
  })();

  // Standalone /aiDesigns(/:id) → Compás (misma estructura árbol + detalle).
  useEffect(() => {
    if (embedded) return;
    const id = Number(routeIdParam || routeAiDesignId);
    if (Number.isFinite(id) && id > 0) {
      navigate(`/skills/tareas?aiDesign=${id}`, {
        replace: true,
        state: {
          ...(location.state && typeof location.state === "object"
            ? location.state
            : {}),
          fromCompass: true,
          AiDesignId: id,
          MoodboardId: id,
        },
      });
      return;
    }
    navigate("/skills/tareas", { replace: true });
  }, [embedded, routeIdParam, routeAiDesignId, navigate, location.state]);

  const [aiDesignId, setAiDesignId] = useState(() => routeAiDesignId);
  // Solo Brief de usuario; nunca Titulo ni el prompt largo de Sketch (confunde la casilla).
  const [nuevoPrompt, setNuevoPrompt] = useState(
    () => String(compassIn?.Brief || "").trim() || "",
  );
  const [nuevoName, setNuevoName] = useState(
    () => String(compassIn?.Titulo || "").trim() || "",
  );
  const [nuevoEngine, setNuevoEngine] = useState("gpt-image");
  const [nuevoRefs, setNuevoRefs] = useState([]);
  const [refFileDragOver, setRefFileDragOver] = useState(false);
  const refFileDragDepth = useRef(0);
  const [nuevoCreating, setNuevoCreating] = useState(false);
  const [nuevoResult, setNuevoResult] = useState(null);
  const [driveDialogOpen, setDriveDialogOpen] = useState(false);
  const [driveSaving, setDriveSaving] = useState(false);
  const [driveError, setDriveError] = useState("");
  const [downloadFormat, setDownloadFormat] = useState("png");
  const [vectorizingSvg, setVectorizingSvg] = useState(false);
  const [vectorizeError, setVectorizeError] = useState("");
  const [previewZoom, setPreviewZoom] = useState(1);
  const [previewPan, setPreviewPan] = useState({ x: 0, y: 0 });
  const [previewDragging, setPreviewDragging] = useState(false);
  const [previewSurfaceSize, setPreviewSurfaceSize] = useState({ w: 0, h: 0 });
  const previewZoomRef = useRef(1);
  const previewPanRef = useRef({ x: 0, y: 0 });
  const previewPinchRef = useRef(null);
  const previewDragRef = useRef(null);
  const previewSurfaceRef = useRef(null);
  const [replaceRefId, setReplaceRefId] = useState(null);
  const [sketchViewOpen, setSketchViewOpen] = useState(false);
  const [generarMenuOpen, setGenerarMenuOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [fichaOpen, setFichaOpen] = useState(false);
  const [fichaTitulo, setFichaTitulo] = useState("");
  const [fichaCreating, setFichaCreating] = useState(false);
  const [fichaError, setFichaError] = useState("");
  const [fichaExisting, setFichaExisting] = useState([]);
  const [moodOpen, setMoodOpen] = useState(false);
  const [moodTitulo, setMoodTitulo] = useState("");
  const [moodCreating, setMoodCreating] = useState(false);
  const [moodError, setMoodError] = useState("");
  const [moodExisting, setMoodExisting] = useState([]);
  /** Historial de versiones (foto + sketch): ver en centro / reusar como refs. */
  const [genHistory, setGenHistory] = useState([]);
  const [selectedHistoryId, setSelectedHistoryId] = useState(null);
  /** Historial ya volcado a cada ficha/moodboard hijo: { [boardId]: historyItemId[] } */
  const [pushedToBoards, setPushedToBoards] = useState({});
  const pushedToBoardsRef = useRef({});
  const [studioHydrated, setStudioHydrated] = useState(!aiDesignId);
  const persistTimerRef = useRef(null);

  useEffect(() => {
    pushedToBoardsRef.current = pushedToBoards;
  }, [pushedToBoards]);
  const [historyAddingId, setHistoryAddingId] = useState(null);
  const [historyCopiedId, setHistoryCopiedId] = useState(null);
  const [historyError, setHistoryError] = useState("");
  const [historyDeleteItem, setHistoryDeleteItem] = useState(null);
  const [retentionNotice, setRetentionNotice] = useState(null);
  const [retentionDialogOpen, setRetentionDialogOpen] = useState(false);
  const retentionNoticeRef = useRef(null);
  const [imageEngines, setImageEngines] = useState(null);
  const [enginesBusy, setEnginesBusy] = useState(false);
  const [enginesMsg, setEnginesMsg] = useState("");

  const engineLabel = useCallback(
    (id, fallbackKey) =>
      imageEngines?.engines?.find((e) => e.id === id)?.label || t(fallbackKey),
    [imageEngines, t],
  );
  const gptActiveLabel = engineLabel(
    "gpt-image",
    "presentacionesSearch.engines.gptName",
  );
  const fluxActiveLabel = engineLabel(
    "flux",
    "presentacionesSearch.engines.fluxName",
  );
  const nanoActiveLabel = engineLabel(
    "nano-banana",
    "presentacionesSearch.engines.nanoName",
  );
  const newEngineModels = imageEngines?.newModels || [];
  const creditsBlocked = Boolean(imageEngines?.creditsBlocked);
  const creditsBlockedMsg =
    imageEngines?.creditsMessage ||
    t("presentacionesSearch.newDesign.creditsBlocked", {
      defaultValue:
        "Sin créditos en Replicate. Las generaciones están bloqueadas hasta recargar saldo.",
    });
  const openaiCreditsBlocked = Boolean(imageEngines?.openaiCreditsBlocked);
  const openaiCreditsBlockedMsg =
    imageEngines?.openaiCreditsMessage ||
    t("presentacionesSearch.newDesign.openaiCreditsBlocked", {
      defaultValue:
        "Sin cuota en OpenAI. Meetings, gastos y embeddings están bloqueados hasta recargar saldo.",
    });

  const nuevoRefsPreview = useMemo(
    () =>
      (nuevoRefs || []).map((item) => ({
        id: item.id,
        file: item.file,
        url: URL.createObjectURL(item.file),
      })),
    [nuevoRefs],
  );

  const API_URL = process.env.REACT_APP_API_URL;

  const apiErrorMessage = useCallback(
    (e) =>
      e?.response?.data?.error ||
      e?.response?.data?.details ||
      e?.message ||
      t("presentacionesSearch.common.unknownError"),
    [t],
  );

  useEffect(
    () => () => {
      nuevoRefsPreview.forEach((item) => URL.revokeObjectURL(item.url));
    },
    [nuevoRefsPreview],
  );

  const applyEnginesCatalog = useCallback((data) => {
    if (data) setImageEngines(data);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getImageEngines();
        if (!cancelled) applyEnginesCatalog(data);
      } catch {
        /* AI-SERVICE offline: labels i18n siguen valiendo */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applyEnginesCatalog]);

  const handleCheckEngines = useCallback(async () => {
    setEnginesBusy(true);
    setEnginesMsg("");
    try {
      const data = await checkImageEngines();
      applyEnginesCatalog(data);
    } catch (e) {
      setEnginesMsg(apiErrorMessage(e));
    } finally {
      setEnginesBusy(false);
    }
  }, [apiErrorMessage, applyEnginesCatalog]);

  const handleActivateEngine = useCallback(
    async (replicateModel, engine) => {
      if (!replicateModel) return;
      setEnginesBusy(true);
      setEnginesMsg("");
      try {
        const data = await activateImageEngine(replicateModel, engine);
        applyEnginesCatalog(data);
      } catch (e) {
        setEnginesMsg(apiErrorMessage(e));
      } finally {
        setEnginesBusy(false);
      }
    },
    [apiErrorMessage, applyEnginesCatalog],
  );

  const handleDismissEngine = useCallback(
    async (replicateModel, engine) => {
      if (!replicateModel) return;
      setEnginesBusy(true);
      setEnginesMsg("");
      try {
        const data = await dismissImageEngine(replicateModel, engine);
        applyEnginesCatalog(data);
      } catch (e) {
        setEnginesMsg(apiErrorMessage(e));
      } finally {
        setEnginesBusy(false);
      }
    },
    [apiErrorMessage, applyEnginesCatalog],
  );

  const handleClearCreditsBlock = useCallback(async () => {
    setEnginesBusy(true);
    setEnginesMsg("");
    try {
      const data = await clearImageEnginesCreditsBlock();
      applyEnginesCatalog(data);
    } catch (e) {
      setEnginesMsg(apiErrorMessage(e));
    } finally {
      setEnginesBusy(false);
    }
  }, [apiErrorMessage, applyEnginesCatalog]);

  const handleClearOpenAiCreditsBlock = useCallback(async () => {
    setEnginesBusy(true);
    setEnginesMsg("");
    try {
      const data = await clearOpenAiCreditsBlock();
      applyEnginesCatalog(data);
    } catch (e) {
      setEnginesMsg(apiErrorMessage(e));
    } finally {
      setEnginesBusy(false);
    }
  }, [apiErrorMessage, applyEnginesCatalog]);

  const handleClose = () => {
    if (embedded && typeof onLeave === "function") {
      onLeave();
      return;
    }
    if (location.key !== "default") {
      navigate(-1);
    } else {
      navigate("/presentacionesSearch");
    }
  };

  const hasGenHistory = (genHistory || []).length > 0;

  const compassProjectId =
    compassIn?.ProjectId != null && Number(compassIn.ProjectId) > 0
      ? Number(compassIn.ProjectId)
      : null;
  const compassMeetingId =
    compassIn?.MeetingSessionId != null &&
    Number(compassIn.MeetingSessionId) > 0
      ? Number(compassIn.MeetingSessionId)
      : null;

  useEffect(() => {
    retentionNoticeRef.current = retentionNotice;
  }, [retentionNotice]);

  const persistStudioState = useCallback(
    async (boardId, history, selId, prompt, name, pushedMap) => {
      if (!boardId) return;
      try {
        await updateSkillAgentMoodboard(boardId, {
          ResultSummary: buildAiDesignStudioSummary({
            designKind: compassIn?.designKind || null,
            prompt,
            name,
            genHistory: history,
            selectedHistoryId: selId,
            pushedToBoards:
              pushedMap != null ? pushedMap : pushedToBoardsRef.current,
            retentionNotice: retentionNoticeRef.current,
          }),
          Titulo: String(name || "").trim() || undefined,
        });
      } catch (err) {
        console.warn("aiDesigns persist studio:", err?.message || err);
      }
    },
    [compassIn?.designKind],
  );

  const ensureAiDesignBoard = useCallback(async () => {
    if (aiDesignId) return aiDesignId;
    const titulo =
      String(nuevoName || compassIn?.Titulo || "").trim() ||
      t("presentacionesSearch.newDesign.title", {
        defaultValue: "AI Designs",
      });
    const created = await createSkillAgentMoodboard({
      Titulo: titulo,
      CategoryLabel: compassIn?.designKind || "nuevos_disenos",
      Kind: "ai_design",
      CustomerId: compassIn?.CustomerId || null,
      Temporada: compassIn?.Temporada || null,
      ProjectId: compassProjectId,
      MeetingSessionId: compassMeetingId,
      ResultSummary: buildAiDesignStudioSummary({
        designKind: compassIn?.designKind || null,
        prompt: nuevoPrompt,
        name: nuevoName,
        genHistory,
        selectedHistoryId,
      }),
    });
    if (created?.id) {
      setAiDesignId(created.id);
      onDesignIdChange?.(created.id);
      if (!embedded) {
        navigate(`/aiDesigns/${created.id}`, {
          replace: true,
          state: {
            ...(location.state && typeof location.state === "object"
              ? location.state
              : {}),
            fromCompass: true,
            AiDesignId: created.id,
            MoodboardId: created.id,
          },
        });
      }
      return created.id;
    }
    return null;
  }, [
    aiDesignId,
    nuevoName,
    nuevoPrompt,
    genHistory,
    selectedHistoryId,
    compassIn,
    compassProjectId,
    compassMeetingId,
    navigate,
    location.state,
    t,
    embedded,
    onDesignIdChange,
  ]);

  // Mantener /aiDesigns/:id en la barra (solo página standalone).
  useEffect(() => {
    if (embedded) return;
    if (!aiDesignId) return;
    const want = `/aiDesigns/${aiDesignId}`;
    if (location.pathname === want) return;
    navigate(want, {
      replace: true,
      state: location.state,
    });
  }, [embedded, aiDesignId, location.pathname, location.state, navigate]);

  useEffect(() => {
    if (!embedded) return;
    const id = Number(designIdProp);
    if (!Number.isFinite(id) || id <= 0) return;
    if (Number(aiDesignId) === id) return;
    setAiDesignId(id);
  }, [embedded, designIdProp, aiDesignId]);

  useEffect(() => {
    let cancelled = false;
    const id = Number(
      routeIdParam ||
        compassIn?.AiDesignId ||
        compassIn?.MoodboardId ||
        aiDesignId,
    );
    if (!Number.isFinite(id) || id <= 0) {
      setStudioHydrated(true);
      return undefined;
    }
    setStudioHydrated(false);
    (async () => {
      try {
        const row = await getSkillAgentMoodboardById(id);
        if (cancelled || !row) return;
        setAiDesignId(Number(row.id));
        setGenHistory([]);
        setSelectedHistoryId(null);
        setNuevoResult(null);
        setPushedToBoards({});
        setRetentionNotice(null);
        setRetentionDialogOpen(false);
        const studio = parseAiDesignStudioSummary(row.ResultSummary);
        if (studio) {
          if (studio.genHistory?.length) setGenHistory(studio.genHistory);
          if (studio.selectedHistoryId)
            setSelectedHistoryId(studio.selectedHistoryId);
          if (studio.pushedToBoards) setPushedToBoards(studio.pushedToBoards);
          if (studio.retentionNotice) {
            setRetentionNotice(studio.retentionNotice);
            if (studio.retentionNotice.pending) setRetentionDialogOpen(true);
          }
          if (studio.prompt && !String(compassIn?.Brief || "").trim()) {
            const saved = String(studio.prompt);
            const looksLikeSketchSystem =
              /fashion flat|dibujo tecnico plano|tech pack|Convierte la prenda/i.test(
                saved,
              );
            if (!looksLikeSketchSystem) {
              setNuevoPrompt(saved);
            }
          }
          if (studio.name && !String(compassIn?.Titulo || "").trim()) {
            setNuevoName(studio.name);
          }
          const last = studio.genHistory?.[0];
          if (last?.relativeUrl || last?.previewUrl) {
            setNuevoResult({
              ok: true,
              filename: last.filename,
              relativeUrl: last.relativeUrl || null,
              pngRelativeUrl: last.pngRelativeUrl || null,
              pngFilename: last.pngFilename || null,
              previewUrl: last.previewUrl || null,
              format: last.format || "png",
              driveFileUrl: last.driveFileUrl || null,
              vectorNote: last.vectorNote || null,
              mode: last.mode || null,
            });
          }
        } else if (row.Titulo && !String(compassIn?.Titulo || "").trim()) {
          setNuevoName(row.Titulo);
        }
        // Contexto Compás mínimo al abrir por URL (refresh / deep link).
        if (!embedded && !compassIn?.fromCompass && row) {
          navigate(`/aiDesigns/${row.id}`, {
            replace: true,
            state: {
              fromCompass: true,
              AiDesignId: row.id,
              MoodboardId: row.id,
              ProjectId: row.ProjectId || null,
              MeetingSessionId: row.MeetingSessionId || null,
              CustomerId: row.CustomerId || null,
              CustomerName: row.CustomerName || "",
              Temporada: row.Temporada || "",
              Titulo: row.Titulo || "",
              designKind: row.CategoryLabel || null,
            },
          });
        }
      } catch (err) {
        console.warn("aiDesigns hydrate:", err?.message || err);
      } finally {
        if (!cancelled) setStudioHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Solo al montar / cambiar id de ruta u origen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeIdParam, compassIn?.AiDesignId, compassIn?.MoodboardId]);

  useEffect(() => {
    if (!studioHydrated || !aiDesignId) return undefined;
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(() => {
      persistStudioState(
        aiDesignId,
        genHistory,
        selectedHistoryId,
        nuevoPrompt,
        nuevoName,
      );
    }, 600);
    return () => {
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    };
  }, [
    studioHydrated,
    aiDesignId,
    genHistory,
    selectedHistoryId,
    nuevoPrompt,
    nuevoName,
    persistStudioState,
  ]);

  const fetchHistoryAsFiles = useCallback(
    async (historyOverride = null) => {
      const history = Array.isArray(historyOverride)
        ? historyOverride
        : genHistory;
      // Refs de bandeja solo si volcamos el historial completo (creación nueva).
      const includeTrayRefs = !Array.isArray(historyOverride);
      const { dibujoUrls, sketchUrls, refFiles } = collectFichaAttachSources(
        history,
        includeTrayRefs ? nuevoRefs : [],
      );
      const historyRefFiles = await filesFromHistoryRefs(history);
      const fetchAsFile = async (item) => {
        const response = await fetch(`${API_URL}/api${item.url}`);
        if (!response.ok) return null;
        const blob = await response.blob();
        return new File([blob], item.filename, {
          type: blob.type || "image/png",
        });
      };
      const dibujoFiles = [
        ...(includeTrayRefs ? refFiles : []),
        ...historyRefFiles,
      ];
      for (const item of dibujoUrls) {
        try {
          const f = await fetchAsFile(item);
          if (f) dibujoFiles.push(f);
        } catch {
          /* skip */
        }
      }
      const sketchFiles = [];
      for (const item of sketchUrls) {
        try {
          const f = await fetchAsFile(item);
          if (f) sketchFiles.push(f);
        } catch {
          /* skip */
        }
      }
      return { dibujoFiles, sketchFiles };
    },
    [genHistory, nuevoRefs, API_URL],
  );

  const loadLinkedBoards = useCallback(
    async (kind) => {
      let sourceId = aiDesignId;
      try {
        if (!sourceId) sourceId = await ensureAiDesignBoard();
      } catch {
        sourceId = aiDesignId;
      }
      if (!sourceId) return [];
      try {
        const rows = await listSkillAgentMoodboards({
          sourceAiDesignId: sourceId,
          kind,
          withAssetCount: 1,
        });
        return (Array.isArray(rows) ? rows : []).filter(
          (r) => Number(r?.id) > 0,
        );
      } catch (err) {
        console.warn("aiDesigns linked boards:", err?.message || err);
        return [];
      }
    },
    [aiDesignId, ensureAiDesignBoard],
  );

  const acknowledgeRetentionDialog = useCallback(async () => {
    setRetentionDialogOpen(false);
    const nextNotice = retentionNotice
      ? {
          ...retentionNotice,
          pending: false,
          acknowledgedAt: new Date().toISOString(),
        }
      : null;
    setRetentionNotice(nextNotice);
    retentionNoticeRef.current = nextNotice;
    if (!aiDesignId) return;
    try {
      const row = await getSkillAgentMoodboardById(aiDesignId);
      const patched = acknowledgeRetentionNoticeInSummary(row?.ResultSummary);
      if (patched) {
        await updateSkillAgentMoodboard(aiDesignId, {
          ResultSummary: patched,
        });
      }
    } catch (err) {
      console.warn("aiDesigns retention ack:", err?.message || err);
    }
  }, [aiDesignId, retentionNotice]);

  const openFichaDialog = async () => {
    setFichaError("");
    setFichaTitulo(
      String(nuevoName || compassIn?.Titulo || "").trim() ||
        t("presentacionesSearch.newDesign.fichaDefaultTitle", {
          defaultValue: "Ficha técnica",
        }),
    );
    setFichaExisting([]);
    setFichaOpen(true);
    const linked = await loadLinkedBoards("ficha_tecnica");
    setFichaExisting(linked);
  };

  const openMoodDialog = async () => {
    setMoodError("");
    setMoodTitulo(
      String(nuevoName || compassIn?.Titulo || "").trim() ||
        t("presentacionesSearch.newDesign.moodboardDefaultTitle", {
          defaultValue: "Moodboard",
        }),
    );
    setMoodExisting([]);
    setMoodOpen(true);
    const linked = await loadLinkedBoards("moodboard");
    setMoodExisting(linked);
  };

  const markHistoryPushedToBoard = useCallback(
    async (boardId, historyIds) => {
      const id = Number(boardId);
      if (!Number.isFinite(id) || id <= 0) return pushedToBoardsRef.current;
      const keys = (historyIds || [])
        .map((x) => String(x || "").trim())
        .filter(Boolean);
      const next = {
        ...pushedToBoardsRef.current,
        [String(id)]: [
          ...new Set([
            ...(pushedToBoardsRef.current[String(id)] || []),
            ...keys,
          ]),
        ],
      };
      setPushedToBoards(next);
      pushedToBoardsRef.current = next;
      const sourceId = aiDesignId || (await ensureAiDesignBoard());
      if (sourceId) {
        await persistStudioState(
          sourceId,
          genHistory,
          selectedHistoryId,
          nuevoPrompt,
          nuevoName,
          next,
        );
      }
      return next;
    },
    [
      aiDesignId,
      ensureAiDesignBoard,
      genHistory,
      selectedHistoryId,
      nuevoPrompt,
      nuevoName,
      persistStudioState,
    ],
  );

  const placeOrphanAssetsOnMoodboard = useCallback(async (boardId) => {
    const id = Number(boardId);
    if (!Number.isFinite(id) || id <= 0) return 0;
    const row = await getSkillAgentMoodboardById(id);
    const assetIds = (Array.isArray(row?.assets) ? row.assets : [])
      .map((a) => Number(a?.id))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (!assetIds.length) return 0;
    const pages = normalizePages(coerceMoodboardPages(row?.PagesJson));
    const { pages: placed, added } = placeMoodboardAssetsOnPages(
      pages,
      assetIds,
      { newSlotId, clampSlot },
    );
    if (!added) return 0;
    await saveSkillAgentMoodboardPages(id, placed);
    return added;
  }, []);

  const syncHistoryIntoBoard = useCallback(
    async (board, historyOverride = null) => {
      if (!board?.id) return;
      const boardId = Number(board.id);
      const asFicha = isFichaTecnicaBoard(board);
      const history = Array.isArray(historyOverride)
        ? historyOverride
        : genHistory;
      const boardKey = String(boardId);
      const tracked = pushedToBoardsRef.current[boardKey];
      const boardCreatedMs =
        Date.parse(board.CreatedAt || board.createdAt || "") || 0;
      const pending = selectPendingHistoryForBoard(
        history,
        tracked,
        boardCreatedMs,
      );

      const sourceId = await ensureAiDesignBoard();
      await persistStudioState(
        sourceId,
        history,
        selectedHistoryId,
        nuevoPrompt,
        nuevoName,
      );

      if (pending.length) {
        const { dibujoFiles, sketchFiles } = await fetchHistoryAsFiles(pending);
        const files = [...dibujoFiles, ...sketchFiles];
        // Tras el primer sync, marcamos TODO el historial como baseline
        // (no solo lo pendiente) para no re-subir lo antiguo en el siguiente.
        const idsToMark =
          tracked == null
            ? (history || []).map((h) => h.id).filter(Boolean)
            : pending.map((h) => h.id).filter(Boolean);

        if (files.length) {
          let dibujoIds = [];
          let sketchIds = [];
          try {
            const attached = await addSkillAgentMoodboardAssets(boardId, {
              files,
            });
            const added = Array.isArray(attached?.added) ? attached.added : [];
            dibujoIds = added
              .slice(0, dibujoFiles.length)
              .map((a) => Number(a.id))
              .filter((n) => n > 0);
            sketchIds = added
              .slice(dibujoFiles.length)
              .map((a) => Number(a.id))
              .filter((n) => n > 0);
          } catch (attachErr) {
            console.warn(
              "sync attach assets:",
              attachErr?.message || attachErr,
            );
            throw attachErr;
          }

          if (asFicha && (dibujoIds.length || sketchIds.length)) {
            try {
              const row = await getSkillAgentMoodboardById(boardId);
              const rawPages = coerceMoodboardPages(row?.PagesJson);
              const pages = normalizePages(
                rawPages.length
                  ? rawPages
                  : buildFichaTecnicaPages({
                      titulo: row?.Titulo || board.Titulo || "Ficha",
                    }),
              );
              const placed = placeFichaAssetsOnPages(pages, {
                dibujoIds,
                sketchIds,
                medidasIds: [],
              });
              await saveSkillAgentMoodboardPages(boardId, placed);
            } catch (placeErr) {
              console.warn("sync place pages:", placeErr?.message || placeErr);
            }
          }
        }

        await markHistoryPushedToBoard(boardId, idsToMark);
      } else if (tracked == null && (history || []).length) {
        await markHistoryPushedToBoard(
          boardId,
          (history || []).map((h) => h.id).filter(Boolean),
        );
      }

      // Moodboard: el API solo auto-coloca si la mesa está vacía. Si ya había
      // fotos, lo nuevo se queda en bandeja — al abrir/sync lo subimos a la mesa.
      if (!asFicha) {
        try {
          await placeOrphanAssetsOnMoodboard(boardId);
        } catch (placeErr) {
          console.warn(
            "sync place moodboard pages:",
            placeErr?.message || placeErr,
          );
        }
      }

      return boardId;
    },
    [
      genHistory,
      ensureAiDesignBoard,
      persistStudioState,
      selectedHistoryId,
      nuevoPrompt,
      nuevoName,
      fetchHistoryAsFiles,
      markHistoryPushedToBoard,
      placeOrphanAssetsOnMoodboard,
    ],
  );

  const syncPendingToLinkedBoards = useCallback(
    async (historyOverride = null) => {
      const history = Array.isArray(historyOverride)
        ? historyOverride
        : genHistory;
      if (!(history || []).length) return;
      let boards = [];
      try {
        const [fichas, moods] = await Promise.all([
          loadLinkedBoards("ficha_tecnica"),
          loadLinkedBoards("moodboard"),
        ]);
        boards = [...(fichas || []), ...(moods || [])];
      } catch (err) {
        console.warn("sync linked boards list:", err?.message || err);
        return;
      }
      if (!boards.length) return;
      for (const board of boards) {
        try {
          await syncHistoryIntoBoard(board, history);
        } catch (err) {
          console.warn("sync linked board", board?.id, err?.message || err);
        }
      }
    },
    [genHistory, loadLinkedBoards, syncHistoryIntoBoard],
  );

  const openExistingBoard = useCallback(
    async (board) => {
      if (!board?.id) return;
      const asFicha = isFichaTecnicaBoard(board);
      const setBusy = asFicha ? setFichaCreating : setMoodCreating;
      const setErr = asFicha ? setFichaError : setMoodError;
      setBusy(true);
      setErr("");
      try {
        await syncHistoryIntoBoard(board);
        setFichaOpen(false);
        setMoodOpen(false);
        navigate(`/skills/tareas?moodboard=${board.id}`);
      } catch (err) {
        setErr(apiErrorMessage(err));
      } finally {
        setBusy(false);
      }
    },
    [syncHistoryIntoBoard, navigate, apiErrorMessage],
  );

  const handleCreateFichaTecnica = async () => {
    if (!hasGenHistory) {
      setFichaError(
        t("presentacionesSearch.newDesign.fichaNeedHistory", {
          defaultValue:
            "Genera al menos una versión (foto o sketch) antes de crear la ficha.",
        }),
      );
      return;
    }
    const titulo = String(fichaTitulo || "").trim();
    if (!titulo) {
      setFichaError(
        t("presentacionesSearch.newDesign.fichaTitleRequired", {
          defaultValue: "Pon un título a la ficha.",
        }),
      );
      return;
    }
    setFichaCreating(true);
    setFichaError("");
    try {
      const sourceId = await ensureAiDesignBoard();
      await persistStudioState(
        sourceId,
        genHistory,
        selectedHistoryId,
        nuevoPrompt,
        nuevoName,
      );
      let pages = buildFichaTecnicaPages({ titulo });
      let chromeStyleJson = null;
      try {
        const resolved = await resolveSkillAgentCaratulaForCreate({
          CustomerId: compassIn?.CustomerId || null,
          boardKind: "ficha_tecnica",
        });
        if (resolved?.PagesJson) pages = resolved.PagesJson;
        if (resolved?.ChromeStyleJson)
          chromeStyleJson = resolved.ChromeStyleJson;
      } catch {
        /* fallback buildFichaTecnicaPages */
      }
      const created = await createSkillAgentMoodboard({
        Titulo: titulo,
        CategoryLabel: titulo,
        Kind: "ficha_tecnica",
        PagesJson: pages,
        ...(chromeStyleJson ? { ChromeStyleJson: chromeStyleJson } : null),
        CustomerId: compassIn?.CustomerId || null,
        Temporada: compassIn?.Temporada || null,
        ProjectId: compassProjectId,
        MeetingSessionId: compassMeetingId,
        SourceAiDesignId: sourceId,
      });

      if (created?.id) {
        const { dibujoFiles, sketchFiles } = await fetchHistoryAsFiles();
        const files = [...dibujoFiles, ...sketchFiles];
        let dibujoIds = [];
        let sketchIds = [];
        if (files.length) {
          try {
            const attached = await addSkillAgentMoodboardAssets(created.id, {
              files,
            });
            const added = Array.isArray(attached?.added) ? attached.added : [];
            dibujoIds = added
              .slice(0, dibujoFiles.length)
              .map((a) => Number(a.id))
              .filter((n) => n > 0);
            sketchIds = added
              .slice(dibujoFiles.length)
              .map((a) => Number(a.id))
              .filter((n) => n > 0);
          } catch (attachErr) {
            console.warn(
              "ficha attach assets:",
              attachErr?.message || attachErr,
            );
          }
        }
        if (dibujoIds.length || sketchIds.length) {
          try {
            const placed = placeFichaAssetsOnPages(pages, {
              dibujoIds,
              sketchIds,
              medidasIds: [],
            });
            await saveSkillAgentMoodboardPages(created.id, placed);
          } catch (placeErr) {
            console.warn("ficha place pages:", placeErr?.message || placeErr);
          }
        }
        await markHistoryPushedToBoard(
          created.id,
          (genHistory || []).map((h) => h.id).filter(Boolean),
        );
        setFichaOpen(false);
        navigate(`/skills/tareas?moodboard=${created.id}`);
      }
    } catch (err) {
      setFichaError(apiErrorMessage(err));
    } finally {
      setFichaCreating(false);
    }
  };

  const handleCreateMoodboard = async () => {
    if (!hasGenHistory) {
      setMoodError(
        t("presentacionesSearch.newDesign.fichaNeedHistory", {
          defaultValue:
            "Genera al menos una versión (foto o sketch) antes de crear el moodboard.",
        }),
      );
      return;
    }
    const titulo = String(moodTitulo || "").trim();
    if (!titulo) {
      setMoodError(
        t("presentacionesSearch.newDesign.moodboardTitleRequired", {
          defaultValue: "Pon un título al moodboard.",
        }),
      );
      return;
    }
    setMoodCreating(true);
    setMoodError("");
    try {
      const sourceId = await ensureAiDesignBoard();
      await persistStudioState(
        sourceId,
        genHistory,
        selectedHistoryId,
        nuevoPrompt,
        nuevoName,
      );
      const created = await createSkillAgentMoodboard({
        Titulo: titulo,
        CategoryLabel: titulo,
        Kind: "moodboard",
        CustomerId: compassIn?.CustomerId || null,
        Temporada: compassIn?.Temporada || null,
        ProjectId: compassProjectId,
        MeetingSessionId: compassMeetingId,
        SourceAiDesignId: sourceId,
      });
      if (created?.id) {
        const { dibujoFiles, sketchFiles } = await fetchHistoryAsFiles();
        const files = [...dibujoFiles, ...sketchFiles];
        if (files.length) {
          try {
            await addSkillAgentMoodboardAssets(created.id, { files });
          } catch (attachErr) {
            console.warn(
              "moodboard attach assets:",
              attachErr?.message || attachErr,
            );
          }
        }
        await markHistoryPushedToBoard(
          created.id,
          (genHistory || []).map((h) => h.id).filter(Boolean),
        );
        setMoodOpen(false);
        navigate(`/skills/tareas?moodboard=${created.id}`);
      }
    } catch (err) {
      setMoodError(apiErrorMessage(err));
    } finally {
      setMoodCreating(false);
    }
  };

  const fichaTreeHint = useMemo(() => {
    if (compassMeetingId) {
      return t("presentacionesSearch.newDesign.fichaTreeHintMeeting", {
        defaultValue:
          "Se añadirá al árbol bajo esta reunión → FICHAS TÉCNICAS. Podrás volver a este AI Design.",
      });
    }
    if (compassProjectId) {
      return t("presentacionesSearch.newDesign.fichaTreeHintProject", {
        defaultValue:
          "Se añadirá al árbol del proyecto → FICHAS TÉCNICAS. Podrás volver a este AI Design.",
      });
    }
    return t("presentacionesSearch.newDesign.fichaTreeHintNoCompass", {
      defaultValue:
        "Abre AI Designs desde Compás (proyecto o reunión) para colgar la ficha en el árbol.",
    });
  }, [compassMeetingId, compassProjectId, t]);

  const moodTreeHint = useMemo(() => {
    if (compassMeetingId) {
      return t("presentacionesSearch.newDesign.moodboardTreeHintMeeting", {
        defaultValue:
          "Se añadirá bajo esta reunión → MOODBOARDS. Podrás volver a este AI Design.",
      });
    }
    if (compassProjectId) {
      return t("presentacionesSearch.newDesign.moodboardTreeHintProject", {
        defaultValue:
          "Se añadirá en el proyecto → MOODBOARDS. Podrás volver a este AI Design.",
      });
    }
    return t("presentacionesSearch.newDesign.moodboardTreeHintNoCompass", {
      defaultValue:
        "Abre AI Designs desde Compás para colgar el moodboard en el árbol del cliente.",
    });
  }, [compassMeetingId, compassProjectId, t]);

  const pushRefsToHistory = useCallback(
    async (refItems) => {
      if (!refItems?.length) return;
      const entries = [];
      for (const item of refItems) {
        if (!item?.file) continue;
        try {
          const previewUrl = await fileToDataUrl(item.file);
          if (!previewUrl) continue;
          entries.push({
            id: `ref-hist-${item.id}`,
            filename: item.file.name || "ref.png",
            previewUrl,
            file: item.file,
            format: (item.file.type || "").includes("png") ? "png" : "jpeg",
            mode: "ref",
            createdAt: Date.now(),
            refItemId: item.id,
          });
        } catch {
          /* skip unreadables */
        }
      }
      if (!entries.length) return;
      const existingIds = new Set((genHistory || []).map((h) => h.id));
      const fresh = entries.filter((e) => !existingIds.has(e.id));
      const nextHistory = !fresh.length
        ? (genHistory || []).map((h) => {
            const hit = entries.find((e) => e.id === h.id);
            return hit ? { ...h, ...hit } : h;
          })
        : [...fresh, ...(genHistory || [])].slice(0, AI_DESIGN_HISTORY_LIMIT);
      setGenHistory(nextHistory);
      if (fresh.length) {
        // Subidas nuevas → misma ficha/moodboard ya enlazada.
        void syncPendingToLinkedBoards(nextHistory);
      }
      const first = entries[0];
      setSelectedHistoryId(first.id);
      setPreviewZoom(1);
      setNuevoResult({
        ok: true,
        filename: first.filename,
        previewUrl: first.previewUrl,
        relativeUrl: null,
        pngRelativeUrl: null,
        format: first.format || "png",
        driveFileUrl: null,
        vectorNote: null,
        mode: "ref",
      });
    },
    [genHistory, syncPendingToLinkedBoards],
  );

  const addImageFiles = useCallback(
    (fileList) => {
      const files = Array.from(fileList || []).filter(isImageFile);
      if (!files.length) return;
      const items = makeNuevoRefItems(files);
      setNuevoRefs((prev) => [...prev, ...items]);
      pushRefsToHistory(items);
    },
    [pushRefsToHistory],
  );

  const handleNuevoRefs = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    addImageFiles(files);
  };

  const removeNuevoRef = (id) => {
    setNuevoRefs((prev) => prev.filter((item) => item.id !== id));
  };

  const replaceNuevoRef = (id, file) => {
    if (!id || !file) return;
    setNuevoRefs((prev) =>
      prev.map((item) => (item.id === id ? { ...item, file } : item)),
    );
    pushRefsToHistory([{ id, file }]);
  };

  const replaceRefPreview = useMemo(() => {
    if (!replaceRefId) return null;
    const idx = (nuevoRefsPreview || []).findIndex(
      (item) => item.id === replaceRefId,
    );
    if (idx < 0) return null;
    return { ...nuevoRefsPreview[idx], index: idx };
  }, [nuevoRefsPreview, replaceRefId]);

  const handleNuevoRefsDragEnd = (result) => {
    if (!result.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;
    setNuevoRefs((prev) => {
      const copy = [...prev];
      const [item] = copy.splice(from, 1);
      copy.splice(to, 0, item);
      return copy;
    });
  };

  const handleNuevoRefsFromCamera = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    addImageFiles(files);
  };

  const handleRefFileDragEnter = (e) => {
    if (nuevoCreating || !dragHasFiles(e)) return;
    e.preventDefault();
    refFileDragDepth.current += 1;
    setRefFileDragOver(true);
  };

  const handleRefFileDragOver = (e) => {
    if (nuevoCreating || !dragHasFiles(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleRefFileDragLeave = (e) => {
    if (!dragHasFiles(e)) return;
    refFileDragDepth.current = Math.max(0, refFileDragDepth.current - 1);
    if (refFileDragDepth.current === 0) setRefFileDragOver(false);
  };

  const handleRefFileDrop = (e) => {
    if (!dragHasFiles(e)) return;
    e.preventDefault();
    refFileDragDepth.current = 0;
    setRefFileDragOver(false);
    if (nuevoCreating) return;
    addImageFiles(e.dataTransfer.files);
  };

  const handleCrearNuevo = async ({
    mode = "photo",
    viewChoice = "front",
  } = {}) => {
    const isSketch = mode === "sketch";
    if (isSketch && !nuevoRefs.length) return;
    if (!isSketch && !(nuevoPrompt || "").trim()) return;
    if (creditsBlocked) {
      setNuevoResult({ error: creditsBlockedMsg });
      return;
    }

    const engine =
      isSketch && nuevoEngine === "nano-banana" ? "gpt-image" : nuevoEngine;
    const baseName = (nuevoName || "").trim();
    const refs = nuevoRefs.map((item) => item.file);

    setNuevoCreating(true);
    setNuevoResult(null);
    setSelectedHistoryId(null);
    setDriveError("");
    try {
      if (!isSketch) {
        const prompt = (nuevoPrompt || "").trim();
        const resp = await createNuevoDiseno({
          prompt,
          engine,
          mode: "photo",
          name: baseName,
          refs,
        });
        setNuevoResult(resp);
        if (resp?.ok && resp.relativeUrl) {
          const id = `gen-${Date.now()}`;
          const entry = {
            id,
            filename: resp.filename,
            relativeUrl: resp.relativeUrl,
            pngRelativeUrl: resp.pngRelativeUrl || null,
            pngFilename: resp.pngFilename || null,
            format: resp.format || "png",
            driveFileUrl: resp.driveFileUrl || null,
            vectorNote: resp.vectorNote || null,
            prompt,
            mode: "photo",
            createdAt: Date.now(),
          };
          setSelectedHistoryId(id);
          const nextHistory = [entry, ...genHistory].slice(
            0,
            AI_DESIGN_HISTORY_LIMIT,
          );
          setGenHistory(nextHistory);
          // Ficha/moodboard ya creados: volcar lo nuevo sin esperar a reabrir.
          void syncPendingToLinkedBoards(nextHistory);
        }
        return;
      }

      // Sketch: una sola generacion (both = front+back en la misma pagina)
      const view = sketchViewsForChoice(viewChoice)[0] || "front";
      const sketchName = baseName
        ? `${baseName}_${view}`
        : `sketch_${view === "both" ? "front_back" : view}`;
      const sketchPrompt = buildSketchPrompt(view);
      const resp = await createNuevoDiseno({
        prompt: sketchPrompt,
        engine,
        mode: "sketch",
        name: sketchName,
        refs,
      });
      setNuevoResult(resp?.ok ? { ...resp, mode: "sketch", view } : resp);
      if (resp?.ok && resp.relativeUrl) {
        const id = `sketch-${Date.now()}`;
        const entry = {
          id,
          filename: resp.filename,
          relativeUrl: resp.relativeUrl,
          pngRelativeUrl: resp.pngRelativeUrl || null,
          pngFilename: resp.pngFilename || null,
          format: resp.format || "png",
          driveFileUrl: resp.driveFileUrl || null,
          vectorNote: resp.vectorNote || null,
          prompt: sketchPrompt,
          mode: "sketch",
          view,
          createdAt: Date.now(),
        };
        setSelectedHistoryId(id);
        const nextHistory = [entry, ...genHistory].slice(
          0,
          AI_DESIGN_HISTORY_LIMIT,
        );
        setGenHistory(nextHistory);
        void syncPendingToLinkedBoards(nextHistory);
      }
    } catch (err) {
      const data = err?.response?.data;
      if (
        data?.code === "CREDITS_BLOCKED" ||
        data?.creditsBlocked ||
        err?.response?.status === 402
      ) {
        applyEnginesCatalog({
          ...(imageEngines || {}),
          creditsBlocked: true,
          creditsBlockedAt: data?.creditsBlockedAt || new Date().toISOString(),
          creditsMessage:
            data?.creditsMessage || data?.error || creditsBlockedMsg,
        });
      }
      setNuevoResult({ error: apiErrorMessage(err) });
    } finally {
      setNuevoCreating(false);
    }
  };

  const handleSaveResultToDrive = async ({
    temporada,
    customerName,
    folder,
  }) => {
    if (!nuevoResult?.relativeUrl) return;
    setDriveSaving(true);
    setDriveError("");
    try {
      const data = await saveDesignToDrive({
        relativeUrl: nuevoResult.relativeUrl,
        filename: nuevoResult.filename,
        format: nuevoResult.format,
        pngRelativeUrl: nuevoResult.pngRelativeUrl,
        pngFilename: nuevoResult.pngFilename,
        temporada,
        customerName,
        folder,
      });
      setNuevoResult((prev) => ({
        ...(prev || {}),
        driveFileUrl: data.driveFileUrl || null,
        drivePngFileUrl: data.drivePngFileUrl || null,
        driveFolderUrl: data.driveFolderUrl || null,
        drivePath: data.drivePath || null,
        driveNote: data.driveNote || null,
        drivePngNote: data.drivePngNote || null,
      }));
      if (selectedHistoryId) {
        setGenHistory((prev) =>
          prev.map((item) =>
            item.id === selectedHistoryId
              ? {
                  ...item,
                  driveFileUrl: data.driveFileUrl || item.driveFileUrl || null,
                }
              : item,
          ),
        );
      }
      setDriveDialogOpen(false);
    } catch (e) {
      setDriveError(apiErrorMessage(e));
    } finally {
      setDriveSaving(false);
    }
  };

  const downloadDesignFile = async (relativeUrl, filename, mime) => {
    if (!relativeUrl) return;
    try {
      const src = String(relativeUrl).trim();
      const response = await fetch(
        src.startsWith("data:") || /^https?:\/\//i.test(src)
          ? src
          : `${API_URL}/api${src}`,
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = new Blob([await response.arrayBuffer()], {
        type: mime || response.headers.get("content-type") || undefined,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("downloadDesignFile", err);
    }
  };

  const handleDownloadHistoryItem = (item, e) => {
    e?.stopPropagation?.();
    if (!item || nuevoCreating) return;

    const preview = String(item.previewUrl || "").trim();
    if (preview.startsWith("data:") || /^https?:\/\//i.test(preview)) {
      const rawName = String(item.filename || "ref.png");
      const saveName = /\.(png|jpe?g|webp|gif|svg)$/i.test(rawName)
        ? rawName
        : `${rawName}.png`;
      const mime = preview.startsWith("data:")
        ? preview.slice(5, preview.indexOf(";")) || "image/png"
        : /\.svg$/i.test(saveName)
          ? "image/svg+xml"
          : "image/png";
      downloadDesignFile(preview, saveName, mime);
      return;
    }

    const hasSvg =
      item.format === "svg" ||
      /\.svg$/i.test(String(item.relativeUrl || "")) ||
      /\.svg$/i.test(String(item.filename || ""));
    const pngPath = String(
      item.pngRelativeUrl ||
        (item.format === "png" ? item.relativeUrl : "") ||
        "",
    ).trim();

    if (pngPath) {
      const filename = String(
        item.pngFilename || item.filename || "design.png",
      );
      const saveName = filename.toLowerCase().endsWith(".png")
        ? filename
        : `${filename.replace(/\.svg$/i, "")}.png`;
      downloadDesignFile(pngPath, saveName, "image/png");
      return;
    }

    if (hasSvg && item.relativeUrl) {
      const filename = String(item.filename || "sketch.svg");
      const saveName = filename.toLowerCase().endsWith(".svg")
        ? filename
        : `${filename}.svg`;
      downloadDesignFile(item.relativeUrl, saveName, "image/svg+xml");
      return;
    }

    const fallback = String(item.relativeUrl || "").trim();
    if (!fallback) return;
    const filename = String(item.filename || "design.png");
    downloadDesignFile(
      fallback,
      filename,
      /\.svg$/i.test(filename) ? "image/svg+xml" : "image/png",
    );
  };

  const historyItemCanDownload = (item) =>
    Boolean(
      item &&
      (String(item.previewUrl || "").trim() ||
        String(item.pngRelativeUrl || "").trim() ||
        String(item.relativeUrl || "").trim()),
    );

  const historyItemToFile = useCallback(
    async (item) => {
      if (!item) return null;
      if (item.file instanceof File) return item.file;
      const dataUrl = String(item.previewUrl || "").trim();
      if (dataUrl.startsWith("data:")) {
        const res = await fetch(dataUrl);
        const blob = await res.blob();
        return new File([blob], item.filename || "ref.png", {
          type: blob.type || "image/png",
        });
      }
      const path = String(item.pngRelativeUrl || item.relativeUrl || "").trim();
      if (!path) return null;
      const response = await fetch(
        /^https?:\/\//i.test(path) ? path : `${API_URL}/api${path}`,
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const baseName = String(item.filename || "reference.png").replace(
        /\.svg$/i,
        ".png",
      );
      return new File([blob], baseName, {
        type: blob.type || "image/png",
      });
    },
    [API_URL],
  );

  const selectHistoryItem = useCallback((item) => {
    if (!item?.relativeUrl && !item?.previewUrl && !item?.pngRelativeUrl)
      return;
    setSelectedHistoryId(item.id);
    setPreviewZoom(1);
    setDriveError("");
    const format =
      item.format ||
      (item.mode === "sketch" || /\.svg$/i.test(item.filename || "")
        ? "svg"
        : "png");
    setNuevoResult({
      ok: true,
      filename: item.filename,
      relativeUrl: item.relativeUrl || null,
      pngRelativeUrl: item.pngRelativeUrl || null,
      pngFilename: item.pngFilename || null,
      previewUrl: item.previewUrl || null,
      format,
      driveFileUrl: item.driveFileUrl || null,
      vectorNote: item.vectorNote || null,
      mode: item.mode || null,
    });
  }, []);

  const copyHistoryPrompt = async (item, e) => {
    e?.stopPropagation?.();
    const text = String(item?.prompt || "").trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setHistoryCopiedId(item.id);
      window.setTimeout(() => {
        setHistoryCopiedId((cur) => (cur === item.id ? null : cur));
      }, 1600);
    } catch (err) {
      console.error("copyHistoryPrompt", err);
    }
  };

  /** Pone la miniatura del historial en la bandeja de refs (hace falta para Sketch / Flux). */
  const addHistoryAsRef = async (item, { silent = false } = {}) => {
    if (
      !item ||
      historyAddingId ||
      (!item.relativeUrl &&
        !item.pngRelativeUrl &&
        !item.previewUrl &&
        !(item.file instanceof File))
    ) {
      return false;
    }
    if (!silent) {
      setHistoryError("");
      setHistoryAddingId(item.id);
    }
    try {
      // Ya está en la bandeja (misma subida).
      if (
        item.refItemId &&
        nuevoRefs.some((r) => String(r.id) === String(item.refItemId))
      ) {
        selectHistoryItem(item);
        return true;
      }
      const file = await historyItemToFile(item);
      if (!file) throw new Error("no file");
      const items = makeNuevoRefItems([file]);
      setNuevoRefs((prev) => [...prev, ...items]);
      if (item.mode === "ref") {
        // Reutilizar entrada de historial; enlazar al nuevo id de bandeja.
        const previewUrl =
          item.previewUrl || (await fileToDataUrl(file).catch(() => null));
        setGenHistory((prev) =>
          prev.map((h) =>
            h.id === item.id
              ? {
                  ...h,
                  file,
                  previewUrl: previewUrl || h.previewUrl,
                  refItemId: items[0].id,
                }
              : h,
          ),
        );
        selectHistoryItem({
          ...item,
          file,
          previewUrl: previewUrl || item.previewUrl,
          refItemId: items[0].id,
        });
      } else {
        await pushRefsToHistory(items);
      }
      return true;
    } catch (err) {
      console.error("addHistoryAsRef", err);
      if (!silent) {
        setHistoryError(t("presentacionesSearch.newDesign.historyUseError"));
      }
      return false;
    } finally {
      if (!silent) setHistoryAddingId(null);
    }
  };

  const requestRemoveHistoryItem = useCallback(
    (item, e) => {
      e?.stopPropagation?.();
      if (!item?.id || nuevoCreating) return;
      setHistoryDeleteItem(item);
    },
    [nuevoCreating],
  );

  const confirmRemoveHistoryItem = useCallback(() => {
    const item = historyDeleteItem;
    if (!item?.id) {
      setHistoryDeleteItem(null);
      return;
    }
    setGenHistory((prev) => prev.filter((h) => h.id !== item.id));
    if (item.refItemId) {
      setNuevoRefs((prev) =>
        prev.filter((r) => String(r.id) !== String(item.refItemId)),
      );
    }
    if (selectedHistoryId === item.id) {
      setSelectedHistoryId(null);
      setNuevoResult(null);
    }
    setHistoryDeleteItem(null);
  }, [historyDeleteItem, selectedHistoryId]);

  const nuevoReady =
    Boolean((nuevoPrompt || "").trim()) &&
    (nuevoEngine !== "flux" || nuevoRefs.length > 0);

  const nuevoSketchReady = nuevoRefs.length > 0;

  const resultHasSvg = Boolean(
    nuevoResult?.ok &&
    nuevoResult.format === "svg" &&
    nuevoResult.relativeUrl &&
    (/\.svg$/i.test(nuevoResult.relativeUrl) ||
      /\.svg$/i.test(nuevoResult.filename || "")),
  );
  const resultPngUrl =
    nuevoResult?.pngRelativeUrl ||
    (nuevoResult?.ok && nuevoResult.format === "png" && nuevoResult.relativeUrl
      ? nuevoResult.relativeUrl
      : null);
  const showGenerarSvg = Boolean(
    nuevoResult?.ok &&
    resultPngUrl &&
    !resultHasSvg &&
    (nuevoResult.mode === "sketch" ||
      /vectorton|generar svg|png listo/i.test(
        String(nuevoResult.vectorNote || ""),
      ) ||
      /sketch|_both_|_front_|_back_/i.test(
        String(nuevoResult.filename || nuevoResult.pngFilename || ""),
      )),
  );
  const compassChip = compassIn
    ? [compassIn.CustomerName, compassIn.Temporada].filter(Boolean).join(" · ")
    : "";

  useEffect(() => {
    if (!nuevoResult?.ok) return;
    setPreviewZoom(1);
    setPreviewPan({ x: 0, y: 0 });
    setDownloadFormat(resultHasSvg ? "svg" : "png");
    setVectorizeError("");
  }, [nuevoResult, resultHasSvg]);

  const clampPreviewZoom = useCallback((z) => {
    const n = Number(z);
    if (!Number.isFinite(n)) return 1;
    return Math.min(2.4, Math.max(0.6, Number(n.toFixed(2))));
  }, []);

  const setPreviewZoomSafe = useCallback(
    (next) => {
      const z = clampPreviewZoom(
        typeof next === "function" ? next(previewZoomRef.current) : next,
      );
      previewZoomRef.current = z;
      setPreviewZoom(z);
      if (z === 1) {
        previewPanRef.current = { x: 0, y: 0 };
        setPreviewPan({ x: 0, y: 0 });
      }
    },
    [clampPreviewZoom],
  );

  const nudgePreviewZoom = useCallback(
    (delta) => {
      setPreviewZoomSafe((z) => z + delta);
    },
    [setPreviewZoomSafe],
  );

  useEffect(() => {
    previewZoomRef.current = previewZoom;
  }, [previewZoom]);

  useEffect(() => {
    previewPanRef.current = previewPan;
  }, [previewPan]);

  useEffect(() => {
    const el = previewSurfaceRef.current;
    if (!el || !nuevoResult?.ok) return undefined;

    const syncSize = () => {
      const padX = 12;
      const padY = 12;
      setPreviewSurfaceSize({
        w: Math.max(0, el.clientWidth - padX),
        h: Math.max(0, el.clientHeight - padY),
      });
    };
    syncSize();
    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(syncSize)
        : null;
    ro?.observe(el);

    const onWheel = (e) => {
      if (!e.altKey) return;
      e.preventDefault();
      const step = e.deltaY > 0 ? -0.1 : 0.1;
      nudgePreviewZoom(step);
    };

    const endDrag = () => {
      previewDragRef.current = null;
      setPreviewDragging(false);
    };

    const onPointerDown = (e) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      if (previewPinchRef.current) return;
      previewDragRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        originX: previewPanRef.current.x,
        originY: previewPanRef.current.y,
        moved: false,
      };
      try {
        el.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      setPreviewDragging(true);
    };

    const onPointerMove = (e) => {
      const drag = previewDragRef.current;
      if (!drag || drag.pointerId !== e.pointerId) return;
      if (previewPinchRef.current) return;
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      if (!drag.moved && Math.hypot(dx, dy) < 3) return;
      drag.moved = true;
      e.preventDefault();
      const next = { x: drag.originX + dx, y: drag.originY + dy };
      previewPanRef.current = next;
      setPreviewPan(next);
    };

    const onPointerUp = (e) => {
      if (
        previewDragRef.current &&
        previewDragRef.current.pointerId === e.pointerId
      ) {
        endDrag();
      }
    };

    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        previewDragRef.current = null;
        setPreviewDragging(false);
        const [a, b] = e.touches;
        const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
        previewPinchRef.current = {
          dist,
          zoom: previewZoomRef.current,
          panX: previewPanRef.current.x,
          panY: previewPanRef.current.y,
        };
        return;
      }
      previewPinchRef.current = null;
    };

    const onTouchMove = (e) => {
      if (e.touches.length !== 2 || !previewPinchRef.current) return;
      e.preventDefault();
      const [a, b] = e.touches;
      const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      const { dist: startDist, zoom: startZoom } = previewPinchRef.current;
      if (!startDist) return;
      setPreviewZoomSafe(startZoom * (dist / startDist));
    };

    const onTouchEnd = (e) => {
      if (e.touches.length < 2) previewPinchRef.current = null;
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", onPointerUp);
    el.addEventListener("pointercancel", onPointerUp);
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd);
    el.addEventListener("touchcancel", onTouchEnd);
    return () => {
      ro?.disconnect();
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("pointercancel", onPointerUp);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [nudgePreviewZoom, setPreviewZoomSafe, nuevoResult?.ok]);

  const handlePreviewDoubleClick = useCallback(() => {
    setPreviewZoomSafe(1);
    previewPanRef.current = { x: 0, y: 0 };
    setPreviewPan({ x: 0, y: 0 });
  }, [setPreviewZoomSafe]);

  const handleGenerarSvg = async () => {
    if (!nuevoResult?.ok || !resultPngUrl || vectorizingSvg) return;
    setVectorizingSvg(true);
    setVectorizeError("");
    try {
      const data = await vectorizeDesignSvg({
        pngRelativeUrl: resultPngUrl,
        name: nuevoName || nuevoResult.pngFilename || nuevoResult.filename,
        cache: nuevoResult.vectortonCache || null,
      });
      if (!data?.ok || !data.relativeUrl) {
        throw new Error(
          data?.error ||
            t("presentacionesSearch.newDesign.generateSvgError", {
              defaultValue: "No se pudo generar el SVG.",
            }),
        );
      }
      const next = {
        ...(nuevoResult || {}),
        ok: true,
        format: "svg",
        filename: data.filename || nuevoResult.filename,
        relativeUrl: data.relativeUrl,
        pngRelativeUrl: data.pngRelativeUrl || resultPngUrl,
        pngFilename:
          data.pngFilename || nuevoResult.pngFilename || nuevoResult.filename,
        vectorNote: data.vectorNote || null,
        engineVersion: data.engineVersion || null,
        vectortonCache: data.cache || null,
      };
      setNuevoResult(next);
      setDownloadFormat("svg");
      if (selectedHistoryId) {
        setGenHistory((prev) =>
          prev.map((item) =>
            item.id === selectedHistoryId
              ? {
                  ...item,
                  format: "svg",
                  filename: next.filename,
                  relativeUrl: next.relativeUrl,
                  pngRelativeUrl: next.pngRelativeUrl,
                  pngFilename: next.pngFilename,
                  vectorNote: next.vectorNote,
                  vectortonCache: next.vectortonCache,
                }
              : item,
          ),
        );
      }
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        t("presentacionesSearch.newDesign.generateSvgError", {
          defaultValue: "No se pudo generar el SVG.",
        });
      setVectorizeError(String(msg));
    } finally {
      setVectorizingSvg(false);
    }
  };

  const handleDownloadResult = () => {
    if (!nuevoResult?.ok) return;
    const wantSvg =
      downloadFormat === "svg" && resultHasSvg && nuevoResult.relativeUrl;
    if (wantSvg) {
      const filename = String(nuevoResult.filename || "sketch.svg");
      const saveName = filename.toLowerCase().endsWith(".svg")
        ? filename
        : `${filename}.svg`;
      downloadDesignFile(nuevoResult.relativeUrl, saveName, "image/svg+xml");
      return;
    }
    const pngUrl = resultPngUrl || nuevoResult.relativeUrl;
    if (!pngUrl) return;
    const filename = String(
      nuevoResult.pngFilename || nuevoResult.filename || "design.png",
    );
    const saveName = filename.toLowerCase().endsWith(".png")
      ? filename
      : `${filename.replace(/\.svg$/i, "")}.png`;
    downloadDesignFile(pngUrl, saveName, "image/png");
  };

  if (!embedded) {
    return null;
  }

  return (
    <Box
      sx={{
        height: "100%",
        maxHeight: "100%",
        minHeight: 0,
        flex: 1,
        overflow: "hidden",
        bgcolor: s.pageBg,
        color: s.text,
        backgroundImage: "none",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        width: "100%",
        maxWidth: "100%",
      }}
    >
      <Box
        sx={{
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            minHeight: 52,
            px: { xs: 1, md: 1.5 },
            py: 0.75,
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            borderBottom: `1px solid ${s.line}`,
            bgcolor: s.panelBg,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              flexShrink: 0,
              minWidth: { md: 120 },
            }}
          >
            <IconButton
              onClick={handleClose}
              disabled={nuevoCreating}
              aria-label={t("common.close")}
              size="small"
              sx={{ color: s.text }}
            >
              <CloseIcon />
            </IconButton>
            <AutoAwesomeIcon sx={{ fontSize: 20, color: s.awesome }} />
            {compassChip ? (
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  color: s.muted,
                  display: { xs: "none", md: "inline" },
                }}
              >
                {compassChip}
              </Typography>
            ) : null}
          </Box>

          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 0.75,
              minWidth: 0,
              px: 1,
            }}
          >
            {nuevoResult?.ok &&
            (nuevoResult.relativeUrl || nuevoResult.pngRelativeUrl) ? (
              <>
                {showGenerarSvg ? (
                  <Button
                    size="small"
                    variant="contained"
                    disabled={vectorizingSvg}
                    onClick={handleGenerarSvg}
                    startIcon={
                      vectorizingSvg ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <AutoAwesomeIcon sx={{ fontSize: 16 }} />
                      )
                    }
                    sx={{
                      height: 36,
                      px: 1.75,
                      textTransform: "none",
                      fontWeight: 700,
                      borderRadius: 999,
                      boxShadow: "none",
                      bgcolor: "#ea580c",
                      color: "#fff",
                      "&:hover": { bgcolor: "#c2410c", boxShadow: "none" },
                    }}
                  >
                    {vectorizingSvg
                      ? t("presentacionesSearch.newDesign.generatingSvg", {
                          defaultValue: "Generando SVG…",
                        })
                      : t("presentacionesSearch.newDesign.generateSvg", {
                          defaultValue: "Generar SVG",
                        })}
                  </Button>
                ) : null}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadOutlinedIcon sx={{ fontSize: 16 }} />}
                  onClick={handleDownloadResult}
                  sx={{
                    height: 36,
                    px: 1.75,
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 999,
                    borderColor: s.softBorderStrong,
                    color: s.text,
                  }}
                >
                  {t("presentacionesSearch.newDesign.downloadDesign")}
                </Button>
                {resultHasSvg ? (
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={
                      downloadFormat === "png" && !resultPngUrl
                        ? "svg"
                        : downloadFormat
                    }
                    onChange={(_, value) => {
                      if (value) setDownloadFormat(value);
                    }}
                    sx={{
                      height: 36,
                      bgcolor: s.isDark ? alpha("#fff", 0.04) : "#fff",
                      "& .MuiToggleButtonGroup-grouped": {
                        height: 36,
                        m: 0,
                      },
                      "& .MuiToggleButton-root": {
                        height: 36,
                        px: 1.5,
                        py: 0,
                        textTransform: "none",
                        fontWeight: 800,
                        lineHeight: 1,
                        borderColor: s.softBorder,
                        color: s.muted,
                        "&.Mui-selected": {
                          color: s.blue,
                          bgcolor: alpha(s.blue, 0.12),
                        },
                      },
                    }}
                  >
                    <ToggleButton value="svg">SVG</ToggleButton>
                    {resultPngUrl ? (
                      <ToggleButton value="png">PNG</ToggleButton>
                    ) : null}
                  </ToggleButtonGroup>
                ) : null}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<CloudUploadOutlinedIcon sx={{ fontSize: 16 }} />}
                  onClick={() => {
                    setDriveError("");
                    setDriveDialogOpen(true);
                  }}
                  sx={{
                    height: 36,
                    px: 1.75,
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 999,
                    borderColor: s.softBorderStrong,
                    color: s.text,
                  }}
                >
                  {t("presentacionesSearch.newDesign.saveToDrive")}
                </Button>
              </>
            ) : null}
            <Button
              size="small"
              variant="contained"
              onClick={() => setGenerarMenuOpen(true)}
              disabled={
                nuevoCreating || fichaCreating || moodCreating || creditsBlocked
              }
              sx={{
                minWidth: 100,
                height: 36,
                px: 1.75,
                borderRadius: 999,
                fontWeight: 700,
                textTransform: "none",
                boxShadow: "none",
                bgcolor: s.blue,
                color: "#fff",
                "&:hover": { bgcolor: "#0d66d0", boxShadow: "none" },
                "&.Mui-disabled": {
                  bgcolor: "rgba(20,115,230,0.25)",
                  color: s.disabledBtnFg,
                },
              }}
            >
              {nuevoCreating
                ? t("presentacionesSearch.newDesign.generating")
                : t("presentacionesSearch.newDesign.generateMenu.open", {
                    defaultValue: "Generar",
                  })}
            </Button>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 1,
              flexShrink: 0,
              minWidth: { md: 120 },
            }}
          >
            {nuevoResult?.ok ? (
              <>
                <Typography
                  variant="caption"
                  sx={{
                    color: s.muted,
                    opacity: 0.8,
                    whiteSpace: "nowrap",
                    display: "none",
                    "@media (pointer: fine)": { display: "inline" },
                  }}
                >
                  {t("presentacionesSearch.newDesign.zoomHint", {
                    defaultValue: "Alt + rueda · arrastrar · doble clic = 100%",
                  })}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: s.muted,
                    opacity: 0.8,
                    whiteSpace: "nowrap",
                    display: "none",
                    "@media (pointer: coarse)": { display: "inline" },
                  }}
                >
                  {t("presentacionesSearch.newDesign.zoomHintTouch", {
                    defaultValue: "Pellizcar · arrastrar · doble toque = 100%",
                  })}
                </Typography>
              </>
            ) : null}
            <Tooltip title={t("presentacionesSearch.newDesign.help.open")}>
              <IconButton
                onClick={() => setHelpOpen(true)}
                aria-label={t("presentacionesSearch.newDesign.help.open")}
                size="small"
                sx={{
                  color: s.muted,
                  border: `1px solid ${s.softBorder}`,
                  borderRadius: 1.5,
                  width: 34,
                  height: 34,
                  "&:hover": { color: s.text, bgcolor: s.hoverSoft },
                }}
              >
                <HelpOutlineOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        {vectorizeError ? (
          <Alert
            severity="error"
            onClose={() => setVectorizeError("")}
            sx={{ borderRadius: 0 }}
          >
            {vectorizeError}
          </Alert>
        ) : null}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: { xs: "100%", md: 260 },
                flexShrink: 0,
                overflowY: "auto",
                p: 1.25,
                borderRight: { md: `1px solid ${s.line}` },
                borderBottom: { xs: `1px solid ${s.line}`, md: "none" },
                bgcolor: s.panelBg,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              {creditsBlocked ? (
                <Alert
                  severity="error"
                  sx={{ alignItems: "flex-start" }}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      disabled={enginesBusy}
                      onClick={handleClearCreditsBlock}
                    >
                      {t("presentacionesSearch.newDesign.clearCreditsBlock", {
                        defaultValue: "Ya recargué — desbloquear",
                      })}
                    </Button>
                  }
                >
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    {t("presentacionesSearch.newDesign.creditsBlockedTitle", {
                      defaultValue: "Sin créditos Replicate",
                    })}
                  </Typography>
                </Alert>
              ) : null}
              {openaiCreditsBlocked ? (
                <Alert
                  severity="error"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      disabled={enginesBusy}
                      onClick={handleClearOpenAiCreditsBlock}
                    >
                      {t(
                        "presentacionesSearch.newDesign.clearOpenAiCreditsBlock",
                        { defaultValue: "Ya recargué OpenAI" },
                      )}
                    </Button>
                  }
                >
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    {t(
                      "presentacionesSearch.newDesign.openaiCreditsBlockedTitle",
                      { defaultValue: "Sin cuota OpenAI" },
                    )}
                  </Typography>
                </Alert>
              ) : null}
              {newEngineModels.length > 0
                ? newEngineModels.map((model) => (
                    <Alert
                      key={`${model.engineId || "x"}-${model.replicateModel}`}
                      severity="info"
                      action={
                        <Box
                          sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}
                        >
                          <Button
                            color="inherit"
                            size="small"
                            disabled={enginesBusy}
                            onClick={() =>
                              handleActivateEngine(
                                model.replicateModel,
                                model.engineId,
                              )
                            }
                          >
                            {t("presentacionesSearch.newDesign.engineUse")}
                          </Button>
                          <Button
                            color="inherit"
                            size="small"
                            disabled={enginesBusy}
                            onClick={() =>
                              handleDismissEngine(
                                model.replicateModel,
                                model.engineId,
                              )
                            }
                          >
                            {t("presentacionesSearch.newDesign.engineLater")}
                          </Button>
                        </Box>
                      }
                    >
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>
                        {t("presentacionesSearch.newDesign.engineUpdateTitle")}
                      </Typography>
                    </Alert>
                  ))
                : null}
              {enginesMsg ? (
                <Alert severity="warning" onClose={() => setEnginesMsg("")}>
                  {enginesMsg}
                </Alert>
              ) : null}

              <TextField
                label={t("presentacionesSearch.newDesign.nameOptional")}
                placeholder={t("presentacionesSearch.newDesign.nameOptional")}
                value={nuevoName}
                onChange={(e) => setNuevoName(e.target.value)}
                fullWidth
                size="small"
                sx={s.fieldSx}
              />

              <Box
                onDragEnter={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  refFileDragDepth.current += 1;
                  setRefFileDragOver(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  refFileDragDepth.current = Math.max(
                    0,
                    refFileDragDepth.current - 1,
                  );
                  if (refFileDragDepth.current === 0) setRefFileDragOver(false);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  refFileDragDepth.current = 0;
                  setRefFileDragOver(false);
                  const files = Array.from(e.dataTransfer?.files || []).filter(
                    (f) => f.type?.startsWith("image/"),
                  );
                  if (files.length) addImageFiles(files);
                }}
                sx={{
                  borderRadius: 2,
                  border: `1px dashed ${refFileDragOver ? s.blue : s.softBorder}`,
                  bgcolor: refFileDragOver
                    ? alpha(s.blue, 0.08)
                    : s.isDark
                      ? alpha("#fff", 0.02)
                      : alpha("#000", 0.02),
                  p: 1,
                  flex: 1,
                  minHeight: 120,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    color: s.muted,
                    display: "block",
                    mb: 0.75,
                  }}
                >
                  {t("presentacionesSearch.newDesign.refsTitle", {
                    defaultValue: "Referencias",
                  })}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    flexWrap: "wrap",
                    mb: 0.75,
                  }}
                >
                  <Tooltip
                    title={t("presentacionesSearch.newDesign.takePhoto")}
                  >
                    <span>
                      <IconButton
                        component="label"
                        size="small"
                        disabled={nuevoCreating}
                        sx={{
                          color: s.text,
                          border: `1px solid ${s.softBorder}`,
                          borderRadius: 1.5,
                          width: 34,
                          height: 34,
                        }}
                      >
                        <PhotoCamera sx={{ fontSize: 17 }} />
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          capture="environment"
                          onChange={handleNuevoRefsFromCamera}
                        />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={t("presentacionesSearch.newDesign.uploadGallery")}
                  >
                    <span>
                      <IconButton
                        component="label"
                        size="small"
                        disabled={nuevoCreating}
                        sx={{
                          color: s.text,
                          border: `1px solid ${s.softBorder}`,
                          borderRadius: 1.5,
                          width: 34,
                          height: 34,
                        }}
                      >
                        <CollectionsOutlinedIcon sx={{ fontSize: 17 }} />
                        <input
                          type="file"
                          hidden
                          multiple
                          accept="image/*"
                          onChange={handleNuevoRefs}
                        />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {nuevoRefs.length > 0 ? (
                    <Tooltip
                      title={t("presentacionesSearch.newDesign.clearRefs")}
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={nuevoCreating}
                          onClick={() => setNuevoRefs([])}
                          sx={{
                            color: s.muted,
                            border: `1px solid ${s.disabledBorder}`,
                            borderRadius: 1.5,
                            width: 34,
                            height: 34,
                          }}
                        >
                          <DeleteSweepOutlinedIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : null}
                  <Typography variant="caption" sx={{ color: s.muted }}>
                    {refFileDragOver
                      ? t("presentacionesSearch.newDesign.dropRefsHere", {
                          defaultValue: "Suelta aquí",
                        })
                      : t("presentacionesSearch.newDesign.dragRefsHint", {
                          defaultValue: "Arrastra fotos",
                        })}
                  </Typography>
                </Box>
                <DragDropContext
                  onDragEnd={(result) => {
                    if (!result.destination) return;
                    const from = result.source.index;
                    const to = result.destination.index;
                    if (from === to) return;
                    setNuevoRefs((prev) => {
                      const next = [...prev];
                      const [moved] = next.splice(from, 1);
                      next.splice(to, 0, moved);
                      return next;
                    });
                  }}
                >
                  <Droppable droppableId="studio-refs-left">
                    {(dropProvided) => (
                      <Box
                        ref={dropProvided.innerRef}
                        {...dropProvided.droppableProps}
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.75,
                          overflowY: "auto",
                          flex: 1,
                          minHeight: 64,
                          alignContent: "flex-start",
                        }}
                      >
                        {nuevoRefsPreview.map((item, idx) => (
                          <Draggable
                            key={item.id}
                            draggableId={String(item.id)}
                            index={idx}
                            isDragDisabled={nuevoCreating}
                          >
                            {(provided, snapshot) => (
                              <Box
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                sx={{
                                  position: "relative",
                                  width: 64,
                                  height: 64,
                                  flexShrink: 0,
                                  borderRadius: 1.25,
                                  overflow: "hidden",
                                  border: `1px solid ${s.softBorder}`,
                                  bgcolor: "#fff",
                                  boxShadow: snapshot.isDragging
                                    ? "0 8px 20px rgba(0,0,0,0.35)"
                                    : "none",
                                }}
                              >
                                <Box
                                  component="img"
                                  src={item.url}
                                  alt=""
                                  sx={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    display: "block",
                                  }}
                                />
                                <Box
                                  sx={{
                                    position: "absolute",
                                    top: 2,
                                    left: 2,
                                    minWidth: 14,
                                    height: 14,
                                    borderRadius: 999,
                                    bgcolor: s.blue,
                                    color: "#fff",
                                    fontSize: "0.58rem",
                                    fontWeight: 800,
                                    display: "grid",
                                    placeItems: "center",
                                  }}
                                >
                                  {idx + 1}
                                </Box>
                                <IconButton
                                  size="small"
                                  disabled={nuevoCreating}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeNuevoRef(item.id);
                                  }}
                                  sx={{
                                    position: "absolute",
                                    top: 0,
                                    right: 0,
                                    p: 0.1,
                                    bgcolor: "rgba(0,0,0,0.65)",
                                    color: "#fff",
                                  }}
                                >
                                  <DeleteOutlineIcon sx={{ fontSize: 12 }} />
                                </IconButton>
                              </Box>
                            )}
                          </Draggable>
                        ))}
                        {dropProvided.placeholder}
                      </Box>
                    )}
                  </Droppable>
                </DragDropContext>
              </Box>
            </Box>

            <Box
              sx={{
                flex: 1,
                minHeight: { xs: 200, md: 0 },
                minWidth: 0,
                display: "flex",
                flexDirection: { xs: "column", lg: "row" },
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  background: s.canvasGrad,
                }}
              >
                {nuevoCreating ? (
                  <Box
                    sx={{
                      flex: 1,
                      display: "grid",
                      placeItems: "center",
                      p: 2,
                    }}
                  >
                    <Box sx={{ textAlign: "center" }}>
                      <CircularProgress sx={{ color: s.blue }} />
                      <Typography sx={{ mt: 2, color: s.muted }}>
                        {t("presentacionesSearch.newDesign.generating")}
                      </Typography>
                    </Box>
                  </Box>
                ) : nuevoResult?.error ? (
                  <Box
                    sx={{
                      flex: 1,
                      display: "grid",
                      placeItems: "center",
                      p: 2,
                    }}
                  >
                    <Alert severity="error" sx={{ maxWidth: 480 }}>
                      {t("presentacionesSearch.newDesign.error")}:{" "}
                      {nuevoResult.error}
                    </Alert>
                  </Box>
                ) : nuevoResult?.ok ? (
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      position: "relative",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: 10,
                        left: 10,
                        right: 10,
                        zIndex: 2,
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 1,
                        pointerEvents: "none",
                      }}
                    >
                      <Typography
                        variant="caption"
                        title={nuevoResult.filename}
                        sx={{
                          maxWidth: "78%",
                          px: 1,
                          py: 0.35,
                          borderRadius: 999,
                          bgcolor: s.isDark
                            ? alpha("#000", 0.55)
                            : alpha("#fff", 0.82),
                          border: `1px solid ${s.softBorder}`,
                          color: s.muted,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          backdropFilter: "blur(6px)",
                        }}
                      >
                        {t("presentacionesSearch.newDesign.saved")}:{" "}
                        {nuevoResult.filename}
                        {nuevoResult.vectorNote
                          ? ` · ${nuevoResult.vectorNote}`
                          : ""}
                        {previewZoom !== 1
                          ? ` · ${Math.round(previewZoom * 100)}%`
                          : ""}
                      </Typography>
                      {(nuevoResult.driveFileUrl ||
                        nuevoResult.drivePngNote ||
                        nuevoResult.driveNote) && (
                        <Box
                          sx={{
                            pointerEvents: "auto",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-end",
                            gap: 0.25,
                            maxWidth: "42%",
                          }}
                        >
                          {nuevoResult.driveFileUrl ? (
                            <Link
                              href={nuevoResult.driveFileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="caption"
                              sx={{
                                color: s.awesome,
                                px: 1,
                                py: 0.35,
                                borderRadius: 999,
                                bgcolor: s.isDark
                                  ? alpha("#000", 0.55)
                                  : alpha("#fff", 0.82),
                                border: `1px solid ${s.softBorder}`,
                                backdropFilter: "blur(6px)",
                              }}
                            >
                              {t("presentacionesSearch.newDesign.savedOnDrive")}
                            </Link>
                          ) : null}
                          {(nuevoResult.drivePngNote ||
                            nuevoResult.driveNote) && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#e8a317",
                                textAlign: "right",
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                bgcolor: s.isDark
                                  ? alpha("#000", 0.55)
                                  : alpha("#fff", 0.82),
                              }}
                            >
                              {nuevoResult.drivePngNote ||
                                nuevoResult.driveNote}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>

                    <Box
                      ref={previewSurfaceRef}
                      onDoubleClick={handlePreviewDoubleClick}
                      sx={{
                        flex: 1,
                        minHeight: 0,
                        overflow: "hidden",
                        position: "relative",
                        touchAction: "none",
                        cursor: previewDragging ? "grabbing" : "grab",
                        userSelect: "none",
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          display: "grid",
                          placeItems: "center",
                          p: { xs: 0.5, md: 0.75 },
                          transform: `translate(${previewPan.x}px, ${previewPan.y}px) scale(${previewZoom})`,
                          transformOrigin: "center center",
                          transition: previewDragging
                            ? "none"
                            : "transform 120ms ease",
                          willChange: "transform",
                        }}
                      >
                        <CardMedia
                          component="img"
                          draggable={false}
                          image={designImageSrc(nuevoResult, API_URL)}
                          sx={{
                            width: "auto",
                            height: "auto",
                            maxWidth:
                              previewSurfaceSize.w > 0
                                ? previewSurfaceSize.w
                                : "100%",
                            maxHeight:
                              previewSurfaceSize.h > 0
                                ? previewSurfaceSize.h
                                : "100%",
                            display: "block",
                            objectFit: "contain",
                            borderRadius: 1.25,
                            bgcolor: "#fff",
                            border: `1px solid ${s.line}`,
                            boxShadow: s.isDark
                              ? "0 8px 28px rgba(0,0,0,0.35)"
                              : "0 8px 28px rgba(0,0,0,0.08)",
                            pointerEvents: "none",
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      flex: 1,
                      display: "grid",
                      placeItems: "center",
                      p: 2,
                    }}
                  >
                    <Box sx={{ maxWidth: 460, textAlign: "center", px: 2 }}>
                      <AutoAwesomeIcon
                        sx={{
                          fontSize: 42,
                          color: s.isDark ? "#3d4f73" : alpha(s.blue, 0.35),
                          mb: 1.5,
                        }}
                      />
                      <Typography sx={{ color: s.muted, lineHeight: 1.6 }}>
                        {t("presentacionesSearch.newDesign.intro")}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  width: { xs: "100%", lg: 220 },
                  flexShrink: 0,
                  borderLeft: { lg: `1px solid ${s.line}` },
                  borderTop: { xs: `1px solid ${s.line}`, lg: "none" },
                  bgcolor: s.panelBg,
                  p: 2,
                  overflowY: "auto",
                  maxHeight: { xs: 260, lg: "none" },
                }}
              >
                <Box
                  sx={{
                    mb: genHistory.length === 0 ? 1.75 : 1.25,
                    p: 1.35,
                    borderRadius: 2.25,
                    border: `1px solid ${alpha(s.blue, 0.22)}`,
                    background: `linear-gradient(160deg, ${alpha(s.blue, 0.12)} 0%, ${alpha(
                      s.blue,
                      0.02,
                    )} 70%)`,
                    boxShadow: `0 6px 16px ${alpha(s.blue, 0.08)}`,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.85,
                      mb: 0.7,
                    }}
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: 1.25,
                        display: "grid",
                        placeItems: "center",
                        bgcolor: alpha(s.blue, 0.16),
                        color: s.blue,
                        flexShrink: 0,
                      }}
                    >
                      <CollectionsOutlinedIcon sx={{ fontSize: 16 }} />
                    </Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 800, lineHeight: 1.2 }}
                    >
                      {t("presentacionesSearch.newDesign.historyTitle")}
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: s.muted,
                      lineHeight: 1.35,
                    }}
                  >
                    {t("presentacionesSearch.newDesign.historyLead")}
                  </Typography>
                </Box>

                {historyError && (
                  <Alert severity="warning" sx={{ mb: 1, py: 0 }}>
                    {historyError}
                  </Alert>
                )}

                {genHistory.length === 0 ? (
                  <Box
                    sx={{
                      borderRadius: 2.5,
                      overflow: "hidden",
                      border: `1px solid ${alpha(s.blue, 0.2)}`,
                      background: `linear-gradient(180deg, ${alpha(s.blue, 0.1)} 0%, ${
                        s.isDark ? alpha("#fff", 0.02) : "#fff"
                      } 55%)`,
                    }}
                  >
                    <Box
                      aria-hidden
                      sx={{
                        position: "relative",
                        height: 72,
                        borderBottom: `1px solid ${alpha(s.blue, 0.12)}`,
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          left: 18,
                          top: 18,
                          width: 34,
                          height: 40,
                          borderRadius: 1,
                          bgcolor: s.isDark ? alpha("#fff", 0.08) : "#fff",
                          border: `1.5px solid ${alpha(s.blue, 0.25)}`,
                          boxShadow: `0 4px 10px ${alpha(s.blue, 0.1)}`,
                          animation: `${bob} 2.4s ease-in-out infinite`,
                          ...motionSafe,
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          left: "50%",
                          top: 14,
                          transform: "translateX(-50%)",
                          width: 38,
                          height: 44,
                          borderRadius: 1,
                          bgcolor: alpha(s.blue, 0.1),
                          border: `1.5px dashed ${alpha(s.blue, 0.4)}`,
                          animation: `${bob} 2s ease-in-out infinite`,
                          animationDelay: "0.2s",
                          ...motionSafe,
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          right: 18,
                          top: 18,
                          width: 34,
                          height: 40,
                          borderRadius: 1,
                          bgcolor: s.isDark ? alpha("#fff", 0.06) : "#fff",
                          border: `1.5px solid ${alpha(s.blue, 0.28)}`,
                          boxShadow: `0 4px 10px ${alpha(s.blue, 0.12)}`,
                          animation: `${cloudLift} 2.6s ease-in-out infinite`,
                          ...motionSafe,
                        }}
                      />
                    </Box>
                    <Box sx={{ px: 1.25, py: 1.15 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          color: s.muted,
                          lineHeight: 1.4,
                          fontWeight: 600,
                        }}
                      >
                        {t("presentacionesSearch.newDesign.historyEmpty")}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1.1 }}
                  >
                    {genHistory.map((item, idx) => {
                      const selected = selectedHistoryId === item.id;
                      const thumbSrc = designImageSrc(item, API_URL);
                      const isRef = item.mode === "ref";
                      const alreadyInRefs =
                        isRef &&
                        item.refItemId &&
                        nuevoRefs.some(
                          (r) => String(r.id) === String(item.refItemId),
                        );
                      return (
                        <Box
                          key={item.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            selectHistoryItem(item);
                            if (isRef && !alreadyInRefs) {
                              addHistoryAsRef(item, { silent: true });
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              selectHistoryItem(item);
                              if (isRef && !alreadyInRefs) {
                                addHistoryAsRef(item, { silent: true });
                              }
                            }
                          }}
                          sx={{
                            position: "relative",
                            border: `1.5px solid ${
                              selected ? s.blue : alpha(s.blue, 0.2)
                            }`,
                            borderRadius: 2,
                            overflow: "hidden",
                            bgcolor: s.isDark ? alpha("#fff", 0.03) : "#fff",
                            boxShadow: selected
                              ? `0 6px 16px ${alpha(s.blue, 0.18)}`
                              : `0 4px 12px ${alpha(s.blue, 0.06)}`,
                            cursor: "pointer",
                            outline: "none",
                            transition:
                              "border-color 120ms ease, box-shadow 120ms ease",
                            "&:hover": {
                              borderColor: s.blue,
                            },
                            "&:hover .history-delete-btn, &:hover .history-download-btn":
                              {
                                opacity: 1,
                              },
                          }}
                        >
                          <Box sx={{ position: "relative" }}>
                            <CardMedia
                              component="img"
                              image={thumbSrc}
                              sx={{
                                width: "100%",
                                height: 148,
                                objectFit: "contain",
                                bgcolor: s.wellBg,
                              }}
                            />
                            {historyItemCanDownload(item) ? (
                              <Tooltip
                                title={t(
                                  "presentacionesSearch.newDesign.historyDownload",
                                  { defaultValue: "Descargar" },
                                )}
                              >
                                <IconButton
                                  className="history-download-btn"
                                  size="small"
                                  aria-label={t(
                                    "presentacionesSearch.newDesign.historyDownload",
                                    { defaultValue: "Descargar" },
                                  )}
                                  disabled={nuevoCreating}
                                  onClick={(e) =>
                                    handleDownloadHistoryItem(item, e)
                                  }
                                  sx={{
                                    position: "absolute",
                                    top: 6,
                                    right: 38,
                                    opacity: { xs: 1, md: 0.85 },
                                    width: 28,
                                    height: 28,
                                    bgcolor: alpha("#0f172a", 0.55),
                                    color: "#fff",
                                    "&:hover": {
                                      bgcolor: alpha(s.blue, 0.92),
                                      color: "#fff",
                                    },
                                  }}
                                >
                                  <DownloadOutlinedIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                            <Tooltip
                              title={t(
                                "presentacionesSearch.newDesign.historyDelete",
                                { defaultValue: "Quitar del historial" },
                              )}
                            >
                              <IconButton
                                className="history-delete-btn"
                                size="small"
                                aria-label={t(
                                  "presentacionesSearch.newDesign.historyDelete",
                                  { defaultValue: "Quitar del historial" },
                                )}
                                disabled={nuevoCreating}
                                onClick={(e) =>
                                  requestRemoveHistoryItem(item, e)
                                }
                                sx={{
                                  position: "absolute",
                                  top: 6,
                                  right: 6,
                                  opacity: { xs: 1, md: 0.85 },
                                  width: 28,
                                  height: 28,
                                  bgcolor: alpha("#0f172a", 0.55),
                                  color: "#fff",
                                  "&:hover": {
                                    bgcolor: alpha("#b91c1c", 0.9),
                                    color: "#fff",
                                  },
                                }}
                              >
                                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <Box sx={{ p: 0.9 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: selected ? s.blue : s.muted,
                                display: "block",
                                mb: 0.45,
                                fontWeight: 700,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              title={item.filename}
                            >
                              #{genHistory.length - idx}
                              {isRef
                                ? ` · ${t(
                                    "presentacionesSearch.newDesign.historyRef",
                                    { defaultValue: "Ref" },
                                  )}`
                                : item.mode === "sketch"
                                  ? " · Sketch"
                                  : ""}
                              {selected
                                ? ` · ${t(
                                    "presentacionesSearch.newDesign.historySelected",
                                  )}`
                                : ""}{" "}
                              · {item.filename}
                            </Typography>
                            {item.prompt ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: 0.35,
                                  mb: 0.65,
                                  p: 0.55,
                                  borderRadius: 1.25,
                                  bgcolor: alpha(s.blue, 0.05),
                                  border: `1px solid ${alpha(s.blue, 0.14)}`,
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    color: s.muted,
                                    lineHeight: 1.35,
                                    display: "-webkit-box",
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    wordBreak: "break-word",
                                  }}
                                  title={item.prompt}
                                >
                                  {item.prompt}
                                </Typography>
                                <Tooltip
                                  title={
                                    historyCopiedId === item.id
                                      ? t(
                                          "presentacionesSearch.newDesign.historyPromptCopied",
                                        )
                                      : t(
                                          "presentacionesSearch.newDesign.historyCopyPrompt",
                                        )
                                  }
                                >
                                  <IconButton
                                    size="small"
                                    onClick={(e) => copyHistoryPrompt(item, e)}
                                    aria-label={t(
                                      "presentacionesSearch.newDesign.historyCopyPrompt",
                                    )}
                                    sx={{
                                      flexShrink: 0,
                                      mt: -0.15,
                                      color:
                                        historyCopiedId === item.id
                                          ? "success.main"
                                          : s.blue,
                                    }}
                                  >
                                    {historyCopiedId === item.id ? (
                                      <CheckOutlinedIcon
                                        sx={{ fontSize: 16 }}
                                      />
                                    ) : (
                                      <ContentCopyOutlinedIcon
                                        sx={{ fontSize: 16 }}
                                      />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            ) : null}
                            <Button
                              size="small"
                              fullWidth
                              variant="outlined"
                              disabled={
                                Boolean(historyAddingId) ||
                                nuevoCreating ||
                                alreadyInRefs
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                addHistoryAsRef(item);
                              }}
                              sx={{
                                textTransform: "none",
                                fontSize: "0.7rem",
                                fontWeight: 700,
                                color: alreadyInRefs ? "success.main" : s.blue,
                                borderColor: alreadyInRefs
                                  ? alpha("#2e7d32", 0.4)
                                  : alpha(s.blue, 0.35),
                                bgcolor: alreadyInRefs
                                  ? alpha("#2e7d32", 0.08)
                                  : alpha(s.blue, 0.06),
                                py: 0.35,
                                "&:hover": {
                                  borderColor: s.blue,
                                  bgcolor: alpha(s.blue, 0.12),
                                },
                              }}
                            >
                              {historyAddingId === item.id
                                ? t(
                                    "presentacionesSearch.newDesign.historyUsing",
                                  )
                                : alreadyInRefs
                                  ? t(
                                      "presentacionesSearch.newDesign.historyInRefs",
                                      {
                                        defaultValue: "En referencias",
                                      },
                                    )
                                  : t(
                                      "presentacionesSearch.newDesign.historyUseAsRef",
                                    )}
                            </Button>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              flexShrink: 0,
              px: { xs: 1.25, md: 1.75 },
              py: 0.75,
              borderTop: `1px solid ${s.softBorder}`,
              bgcolor: "#fff",
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "flex-end" },
              gap: 1,
            }}
          >
            <TextField
              value={nuevoPrompt}
              onChange={(e) =>
                setNuevoPrompt(sanitizeStudioPrompt(e.target.value))
              }
              onPaste={(e) => {
                const pasted = e.clipboardData?.getData("text") || "";
                const clean = sanitizeStudioPrompt(pasted);
                if (clean !== pasted.trim()) {
                  e.preventDefault();
                  if (clean) {
                    setNuevoPrompt((prev) =>
                      sanitizeStudioPrompt(`${prev}${clean}`),
                    );
                  }
                }
              }}
              fullWidth
              multiline
              minRows={1}
              maxRows={2}
              label={t("presentacionesSearch.newDesign.promptLabel")}
              placeholder={t(
                "presentacionesSearch.newDesign.promptPlaceholder",
              )}
              size="small"
              sx={{
                ...s.fieldSx,
                flex: 1,
                minWidth: 0,
                "& .MuiInputBase-root": {
                  alignItems: "flex-start",
                  bgcolor: "#ececec",
                  color: "#111",
                },
                "& .MuiInputBase-input": {
                  color: "#111",
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "rgba(0,0,0,0.45)",
                  opacity: 1,
                },
                "& .MuiInputLabel-root": {
                  color: "rgba(0,0,0,0.55)",
                },
              }}
            />

            <Box
              sx={{
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                gap: 0.35,
                pb: { sm: 0.15 },
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  color: s.muted,
                  lineHeight: 1.2,
                }}
              >
                {t("presentacionesSearch.newDesign.engineLabel", {
                  defaultValue: "Motor",
                })}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                {NUEVO_ENGINES.map((engineOption) => {
                  const selected = nuevoEngine === engineOption.id;
                  const shortLabel =
                    engineOption.id === "gpt-image"
                      ? "GPT 2.5"
                      : engineOption.id === "flux"
                        ? "FLUX 3"
                        : engineOption.id === "nano-banana"
                          ? "Nano B Pro"
                          : t(engineOption.labelKey);
                  const fullLabel =
                    engineOption.id === "gpt-image"
                      ? gptActiveLabel
                      : engineOption.id === "flux"
                        ? fluxActiveLabel
                        : engineOption.id === "nano-banana"
                          ? nanoActiveLabel
                          : shortLabel;
                  return (
                    <Tooltip key={engineOption.id} title={fullLabel}>
                      <Button
                        size="small"
                        onClick={() => setNuevoEngine(engineOption.id)}
                        sx={{
                          textTransform: "none",
                          borderRadius: 999,
                          px: 1.1,
                          py: 0.35,
                          minHeight: 28,
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          color: selected ? "#111" : s.text,
                          bgcolor: selected
                            ? engineOption.accent
                            : "transparent",
                          border: "1px solid",
                          borderColor: selected
                            ? engineOption.accent
                            : s.softBorder,
                          "&:hover": {
                            bgcolor: selected
                              ? engineOption.accent
                              : s.hoverSoft,
                          },
                        }}
                      >
                        {shortLabel}
                      </Button>
                    </Tooltip>
                  );
                })}
                <Button
                  size="small"
                  disabled={enginesBusy}
                  onClick={handleCheckEngines}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.7rem",
                    color: s.muted,
                    px: 0.75,
                    minHeight: 28,
                  }}
                >
                  {enginesBusy
                    ? t("presentacionesSearch.newDesign.engineChecking")
                    : t("presentacionesSearch.newDesign.engineCheck")}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <AiDesignsGenerarDialog
        open={generarMenuOpen}
        busy={nuevoCreating || fichaCreating || moodCreating}
        dibujoReady={nuevoReady && !creditsBlocked}
        sketchReady={nuevoSketchReady && !creditsBlocked}
        boardsReady={hasGenHistory}
        dibujoBlockedReason={
          creditsBlocked
            ? creditsBlockedMsg
            : !nuevoReady
              ? t(
                  "presentacionesSearch.newDesign.generateMenu.needPromptRefs",
                  {
                    defaultValue: "Hace falta prompt (y refs si usas FLUX).",
                  },
                )
              : ""
        }
        sketchBlockedReason={
          creditsBlocked
            ? creditsBlockedMsg
            : !nuevoSketchReady
              ? t("presentacionesSearch.newDesign.generateMenu.needRefs", {
                  defaultValue: "Añade al menos una foto de referencia.",
                })
              : ""
        }
        boardsBlockedReason={
          hasGenHistory
            ? ""
            : t("presentacionesSearch.newDesign.fichaNeedHistory", {
                defaultValue:
                  "Genera al menos una versión antes de crear ficha o moodboard.",
              })
        }
        onClose={() => setGenerarMenuOpen(false)}
        onChoose={(choice) => {
          setGenerarMenuOpen(false);
          if (choice === "dibujo") {
            void handleCrearNuevo();
            return;
          }
          if (choice === "sketch") {
            setSketchViewOpen(true);
            return;
          }
          if (choice === "ficha") {
            void openFichaDialog();
            return;
          }
          if (choice === "moodboard") {
            void openMoodDialog();
          }
        }}
      />
      <NuevoDisenoSketchViewDialog
        open={sketchViewOpen}
        submitting={nuevoCreating}
        onClose={() => !nuevoCreating && setSketchViewOpen(false)}
        onConfirm={async (choice) => {
          setSketchViewOpen(false);
          await handleCrearNuevo({ mode: "sketch", viewChoice: choice });
        }}
      />
      <NuevoDisenoHelpDialog
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
      />
      <CreateFichaTecnicaDialog
        open={fichaOpen}
        titulo={fichaTitulo}
        onTituloChange={setFichaTitulo}
        onClose={() => !fichaCreating && setFichaOpen(false)}
        onCreate={handleCreateFichaTecnica}
        creating={fichaCreating}
        error={fichaError}
        treeHint={fichaTreeHint}
        variant="ficha"
        existingBoards={fichaExisting}
        onOpenExisting={openExistingBoard}
      />
      <CreateFichaTecnicaDialog
        open={moodOpen}
        titulo={moodTitulo}
        onTituloChange={setMoodTitulo}
        onClose={() => !moodCreating && setMoodOpen(false)}
        onCreate={handleCreateMoodboard}
        creating={moodCreating}
        error={moodError}
        treeHint={moodTreeHint}
        variant="moodboard"
        existingBoards={moodExisting}
        onOpenExisting={openExistingBoard}
      />
      <HistoryRetentionDialog
        open={retentionDialogOpen}
        purgedCount={retentionNotice?.purgedCount || 0}
        retentionDays={
          retentionNotice?.retentionDays || AI_DESIGN_HISTORY_RETENTION_DAYS
        }
        onClose={acknowledgeRetentionDialog}
      />
      <ConfirmHistoryDeleteDialog
        open={Boolean(historyDeleteItem)}
        item={historyDeleteItem}
        thumbSrc={
          historyDeleteItem ? designImageSrc(historyDeleteItem, API_URL) : null
        }
        onClose={() => setHistoryDeleteItem(null)}
        onConfirm={confirmRemoveHistoryItem}
      />
      <SaveDesignToDriveDialog
        open={driveDialogOpen}
        saving={driveSaving}
        error={driveError}
        onClose={() => {
          if (!driveSaving) setDriveDialogOpen(false);
        }}
        onSave={handleSaveResultToDrive}
      />
      <ReplaceDesignRefDialog
        open={Boolean(replaceRefPreview)}
        previewUrl={replaceRefPreview?.url}
        fileName={replaceRefPreview?.file?.name}
        index={replaceRefPreview?.index}
        onClose={() => setReplaceRefId(null)}
        onReplace={(file) => {
          if (!replaceRefId) return;
          replaceNuevoRef(replaceRefId, file);
          setReplaceRefId(null);
        }}
        onRemove={() => {
          if (!replaceRefId) return;
          removeNuevoRef(replaceRefId);
          setReplaceRefId(null);
        }}
      />
    </Box>
  );
};

export default AiDesignsStudioPage;
